import { renderBlockPendingDeletionNotice } from '@blocksuite/affine-block-streamlain';
import { deleteBlocksThroughGate } from '@blocksuite/affine-block-surface';
import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { DropIndicator } from '@blocksuite/affine-components/drop-indicator';
import { PeekViewProvider } from '@blocksuite/affine-components/peek';
import { toast } from '@blocksuite/affine-components/toast';
import {
  blockPendingDeletionSource,
  blockPendingDeletionState,
  type DatabaseBlockModel,
  type NoteBlockModel,
  NoteDisplayMode,
  subscribeBlockPendingDeletion,
} from '@blocksuite/affine-model';
import { isDatabaseCutPending } from '@blocksuite/affine-shared/adapters';
import { EDGELESS_TOP_CONTENTEDITABLE_SELECTOR } from '@blocksuite/affine-shared/consts';
import {
  BlockElementCommentManager,
  CommentProviderIdentifier,
  DocModeProvider,
  NotificationProvider,
  type NotificationService,
  type TelemetryEventMap,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import { repairActionButton } from '@blocksuite/affine-shared/utils';
import { getDropResult } from '@blocksuite/affine-widget-drag-handle';
import {
  areDataViewRemotePresencesEqual,
  cellPresenceIsForView,
  createRecordDetail,
  createUniComponentFromWebComponent,
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  DATA_SOURCE_UI_WINDOW_MAX_LIMIT,
  DATA_VIEW_CELL_PRESENCE_EVENT,
  DATABASE_LARGE_DATA_POLICY,
  type DatabaseProjectionSyncState,
  type DataViewCellPresenceDetail,
  dataViewI18nT,
  type DataViewRemoteAreaSelectionPresence,
  type DataViewRemoteCellPresence,
  type DataViewRemoteRowFocus,
  DataViewRootUILogic,
  type DataViewSelection,
  type DataViewUILogicBase,
  type DataViewWidget,
  type DataViewWidgetProps,
  defineUniComponent,
  ExternalGroupByConfigProvider,
  getAreaSelectionPresenceFromSelection,
  getCellPresenceFromSelection,
  getColumnIdByViewIndex,
  getRowFocusIdFromSelection,
  getRowIdByViewIndex,
  lazy,
  readAssistantDatabasePresences,
  renderUniLit,
  type SingleView,
  uniMap,
} from '@blocksuite/data-view';
import {
  openDatabaseBulkUploadPicker,
  popViewOptions,
  widgetPresets,
} from '@blocksuite/data-view/widget-presets';
import { Rect, serializeXYWH } from '@blocksuite/global/gfx';
import { watch } from '@blocksuite/global/lit';
import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  CloseIcon,
  CommentIcon,
  CopyIcon,
  DeleteIcon,
  DownloadIcon,
  ExpandFullIcon,
  LayoutIcon,
  LocalDataIcon,
  LockIcon,
  MoreHorizontalIcon,
  UploadIcon,
} from '@blocksuite/icons/lit';
import { type BlockComponent, BlockSelection } from '@blocksuite/std';
import { measureGfxPerformance } from '@blocksuite/std/gfx';
import { RANGE_SYNC_EXCLUDE_ATTR } from '@blocksuite/std/inline';
import { Slice } from '@blocksuite/store';
import {
  autoUpdate,
  computePosition,
  offset,
  shift,
  size,
} from '@floating-ui/dom';
import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';
import {
  FILE_INDEX_HIGHLIGHT_EVENTS,
  FILE_INDEX_HIGHLIGHT_SCOPES,
  FILE_INDEX_ROW_HIGHLIGHT_ATTRIBUTE,
  type FileIndexRowSourceHighlightChangeDetail,
  type FileIndexRowSourceHighlightScope,
  type FileIndexSourceElementHighlightChangeDetail,
  isDatabaseShardDirectoryProp,
} from '@streamlain/database-identity';
import type { PropertyValues } from 'lit';
import { html, nothing, render } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from './canvas-defaults.js';
import { createCanvasPresentationFeatureFlags } from './canvas-presentation-state.js';
import { popSideDetail } from './components/layout.js';
import { DatabaseConfigExtension } from './config.js';
import { EditorHostKey } from './context/host-context.js';
import {
  createDatabaseBlockDataSource,
  type DatabaseMissingStorage,
  type DatabaseRefusedChangeReason,
  type DatabaseRefusedChangeRequest,
  type DatabaseRepairOutcome,
  type DatabaseRepairRequest,
  type DatabaseRowShardReadiness,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
  observeDatabaseSystemDocMissing,
} from './data-source.js';
import { onShardDirectoryComplete } from './data-source-shard-directory.js';
import type { DatabaseAppUsageRead } from './database-app-usage.js';
import {
  databaseBlockStyles,
  databaseCanvasRowWindowFooterStyles,
  databaseContentStyles,
  databaseFullscreenButtonStyles,
  databaseFullscreenRowWindowFooterStyles,
  databaseInlineLargeDataBoundaryStyles,
  databaseInlineLargeDataPreviewStyles,
  databaseOpsStyles,
  databaseRefusedChangesStyles,
  databaseRowWindowFooterStyles,
  databaseRowWindowPageMenuPortalStyles,
  databaseShardErrorStyles,
  databaseStickyRowWindowFooterStyles,
  databaseTitleStyles,
} from './database-block-styles.js';
import { resolveDatabaseBodyReadiness } from './database-body-readiness.js';
import { renderDatabaseDataViewChrome } from './database-data-view-chrome.js';
import {
  acquireDatabaseOfflineCopy,
  type DatabaseOfflineCopy,
} from './database-offline-copy.js';
import { BlockRenderer } from './detail-panel/block-renderer.js';
import { NoteRenderer } from './detail-panel/note-renderer.js';
import {
  getFileIndexRowIdsForSourceBlocks,
  getFileIndexSourceBlockIdsForRows,
  isFileIndexDatabase,
  isHiddenFileIndexDatabase,
} from './file-index-database.js';
import {
  DatabaseSelection,
  type DatabaseSelectionSurface,
} from './selection.js';
import { getSingleDocIdFromText } from './utils/title-doc.js';
import type { DatabaseViewExtensionOptions } from './view';

export function shouldRenderDatabaseFullscreenButton({
  docMode,
  parentFlavour,
  readonly,
}: {
  docMode: string | null | undefined;
  parentFlavour: string | null | undefined;
  readonly: boolean;
}): boolean {
  return docMode === 'edgeless' && parentFlavour !== 'affine:note' && !readonly;
}

type DatabaseSurfaceProfile = 'embedded' | 'fullscreen' | 'page';

type DatabaseSurfaceChromeOptions = {
  forceRowWindowControls: boolean;
  profile: DatabaseSurfaceProfile;
  showInlineLargeDataBoundary: boolean;
  showQuickSettings: boolean;
  showRowWindowFooter: boolean;
  showTitleRow: boolean;
  showToolbarRow: boolean;
  showTools: boolean;
  showViewBar: boolean;
};

function readDatabaseSurfaceBoolean(
  surface: HTMLElement | null,
  key: string,
  fallback: boolean
): boolean {
  const value = surface?.dataset[key];
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

export function shouldRenderDatabaseInlineLargeDataBoundary({
  docMode,
  parentFlavour,
  columnCount,
  rowCount,
}: {
  docMode: string | null | undefined;
  parentFlavour: string | null | undefined;
  columnCount: number;
  rowCount: number;
}): boolean {
  const hasLargeRows =
    Number.isInteger(rowCount) && rowCount > DATA_SOURCE_UI_WINDOW_MAX_LIMIT;
  const hasLargeColumns =
    Number.isInteger(columnCount) &&
    columnCount > DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT;
  if (!hasLargeRows && !hasLargeColumns) {
    return false;
  }
  return docMode === 'page' || parentFlavour === 'affine:note';
}

export function shouldRenderDatabaseInlineLargeDataPreview({
  columnCount,
  docMode,
  isFullscreen,
  parentFlavour,
  rowCount,
}: {
  columnCount: number;
  docMode: string | null | undefined;
  isFullscreen: boolean;
  parentFlavour: string | null | undefined;
  rowCount: number;
}): boolean {
  return (
    !isFullscreen &&
    shouldRenderDatabaseInlineLargeDataBoundary({
      columnCount,
      docMode,
      parentFlavour,
      rowCount,
    })
  );
}

function getDatabaseExportFileName(title: string): string {
  const baseName = title.trim() || 'database';
  return `${baseName.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'database'}.csv`;
}

export type DatabaseWindowControlsState = {
  currentPage: number;
  pageCount: number;
  rowWindowEnd: number;
  rowWindowStart: number;
  showControls: boolean;
  showFilteredProjectionPending: boolean;
  showFilteredVisibleCount: boolean;
  showRowControls: boolean;
};

export type DatabaseWindowControlsLocale = 'de' | 'en';

type DatabaseRowWindowPagePickerPortalModel = {
  currentPage: number;
  locale: DatabaseWindowControlsLocale;
  pageCount: number;
  rowLimit: number;
  selectPageLabel: string;
  view: SingleView;
};

type DatabaseFooterSelection = {
  canDeleteRows: boolean;
  rowIds: string[];
};

export const DATABASE_INITIAL_LOAD_READY_ATTR =
  'data-streamlain-database-initial-load-ready';

const DATABASE_WINDOW_CONTROL_COPY = {
  de: {
    actionsHint: 'Zeilen pro Seite einstellen',
    all: 'Alle',
    cards: 'Karten',
    cardSingular: 'Karte',
    clearSelection: 'Auswahl aufheben',
    deleteSelection: 'Ausgewählte Zeilen löschen',
    events: 'Einträge',
    eventSingular: 'Eintrag',
    filteredPending: 'Gefilterte Summe wird geladen',
    navHint: 'Zwischen Seiten navigieren',
    nextPage: 'Nächste Seite',
    of: 'von',
    page: 'Seite',
    previousPage: 'Vorherige Seite',
    rows: 'Zeilen',
    rowSingular: 'Zeile',
    selectPage: 'Seite auswählen',
    selectedRows: 'ausgewählt',
    summaryHint: 'Zeilenanzahl dieser Ansicht',
    tasks: 'Aufgaben',
    taskSingular: 'Aufgabe',
    updateFailed: 'Konnte nicht aktualisiert werden',
    updating: 'Wird aktualisiert...',
    partial: 'Noch nicht vollständig geladen',
    stale: 'Daten werden neu geladen',
    visible: 'sichtbar',
  },
  en: {
    actionsHint: 'Set rows per page',
    all: 'All',
    cards: 'cards',
    cardSingular: 'card',
    clearSelection: 'Clear selection',
    deleteSelection: 'Delete selected rows',
    events: 'entries',
    eventSingular: 'entry',
    filteredPending: 'Filtered total pending',
    navHint: 'Navigate between pages',
    nextPage: 'Next page',
    of: 'of',
    page: 'Page',
    previousPage: 'Previous page',
    rows: 'rows',
    rowSingular: 'row',
    selectPage: 'Select page',
    selectedRows: 'selected',
    summaryHint: 'Row count for this view',
    tasks: 'tasks',
    taskSingular: 'task',
    updateFailed: 'Could not update',
    updating: 'Updating...',
    partial: 'Not fully loaded yet',
    stale: 'Reloading data',
    visible: 'visible',
  },
} as const;

const DATABASE_ROW_WINDOW_LIMIT_PRESETS = [
  10,
  25,
  50,
  100,
  250,
  DATABASE_LARGE_DATA_POLICY.ui.maxPageSize,
] as const;

/** `managedBy` of the tables the Mail app provisions and fills itself. */
const MAIL_MANAGED_DATABASE_OWNER = 'workspace-mail';

const FILE_INDEX_ROW_HIGHLIGHT_STYLE_ID =
  'streamlain-file-index-row-highlight-style';

function ensureFileIndexRowHighlightStyles(documentRef: Document): void {
  if (documentRef.getElementById(FILE_INDEX_ROW_HIGHLIGHT_STYLE_ID)) return;

  const style = documentRef.createElement('style');
  style.id = FILE_INDEX_ROW_HIGHLIGHT_STYLE_ID;
  style.textContent = `
    [${FILE_INDEX_ROW_HIGHLIGHT_ATTRIBUTE}="true"] {
      background-color: rgba(30, 150, 235, 0.1) !important;
      box-shadow:
        inset 3px 0 0 var(--affine-primary-color, #1e96eb),
        inset 0 0 0 1px rgba(30, 150, 235, 0.24) !important;
    }
  `;
  documentRef.head.append(style);
}

function escapeDataAttributeSelectorValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function resolveDatabaseWindowControlsLocale(
  language: string | null | undefined
): DatabaseWindowControlsLocale {
  return language?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

function getRuntimeDatabaseWindowControlsLocale(): DatabaseWindowControlsLocale {
  const language =
    typeof document !== 'undefined'
      ? document.documentElement.lang
      : typeof navigator !== 'undefined'
        ? navigator.language
        : undefined;
  return resolveDatabaseWindowControlsLocale(language);
}

function formatDatabaseWindowNumber(
  value: number,
  locale: DatabaseWindowControlsLocale
) {
  return new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US').format(
    value
  );
}

export function formatDatabaseWindowPageSizeLabel({
  locale,
  rowLimit,
}: {
  locale: DatabaseWindowControlsLocale;
  rowLimit: number;
}): string {
  const copy = DATABASE_WINDOW_CONTROL_COPY[locale];
  const rowWord = rowLimit === 1 ? copy.rowSingular : copy.rows;
  return `${formatDatabaseWindowNumber(rowLimit, locale)} ${rowWord}`;
}

export function formatDatabaseWindowRowsLabel({
  locale,
  rowCount,
  state,
  viewMode,
}: {
  locale: DatabaseWindowControlsLocale;
  rowCount: number;
  state: DatabaseWindowControlsState;
  viewMode?: string;
}): string {
  const copy = DATABASE_WINDOW_CONTROL_COPY[locale];
  const itemWords =
    viewMode === 'calendar'
      ? {
          plural: copy.events,
          singular: copy.eventSingular,
        }
      : viewMode === 'gantt'
        ? {
            plural: copy.tasks,
            singular: copy.taskSingular,
          }
        : viewMode === 'kanban'
          ? {
              plural: copy.cards,
              singular: copy.cardSingular,
            }
          : {
              plural: copy.rows,
              singular: copy.rowSingular,
            };
  if (rowCount === 0) {
    return `0 ${itemWords.plural}`;
  }
  if (state.pageCount <= 1) {
    const rowWord = rowCount === 1 ? itemWords.singular : itemWords.plural;
    return `${formatDatabaseWindowNumber(rowCount, locale)} ${rowWord}`;
  }
  return `${formatDatabaseWindowNumber(
    state.rowWindowStart,
    locale
  )}-${formatDatabaseWindowNumber(
    state.rowWindowEnd,
    locale
  )} ${copy.of} ${formatDatabaseWindowNumber(rowCount, locale)}`;
}

export function resolveDatabaseWindowProjectionStatusLabel({
  locale,
  projectionComplete,
  projectionSyncState,
}: {
  locale: DatabaseWindowControlsLocale;
  projectionComplete: boolean;
  projectionSyncState: DatabaseProjectionSyncState;
}): string | undefined {
  const copy = DATABASE_WINDOW_CONTROL_COPY[locale];
  if (projectionSyncState === 'unsupported') {
    return copy.updateFailed;
  }
  if (!projectionComplete) {
    if (projectionSyncState === 'partial') return copy.partial;
    if (projectionSyncState === 'stale') return copy.stale;
    return copy.updating;
  }
  return undefined;
}

export function resolveDatabaseWindowControlsState({
  forceWindowControls,
  hasActiveFilterOrSearch,
  projectionComplete = true,
  projectionTotalKnown = true,
  rowCount,
  rowLimit,
  rowOffset,
}: {
  forceWindowControls: boolean;
  hasActiveFilterOrSearch: boolean;
  projectionComplete?: boolean;
  projectionTotalKnown?: boolean;
  rowCount: number;
  rowLimit: number;
  rowOffset: number;
}): DatabaseWindowControlsState {
  const rowWindowEnd = Math.min(rowCount, rowOffset + rowLimit);
  const pageCount =
    rowCount === 0 ? 1 : Math.max(1, Math.ceil(rowCount / rowLimit));
  const currentPage =
    rowCount === 0
      ? 1
      : Math.min(pageCount, Math.floor(rowOffset / rowLimit) + 1);
  const showRowControls =
    forceWindowControls ||
    rowCount > DATA_SOURCE_UI_WINDOW_MAX_LIMIT ||
    hasActiveFilterOrSearch;
  const showFilteredProjectionPending =
    hasActiveFilterOrSearch && (!projectionComplete || !projectionTotalKnown);
  const showFilteredVisibleCount =
    hasActiveFilterOrSearch && !showFilteredProjectionPending;

  return {
    currentPage,
    pageCount,
    rowWindowEnd,
    rowWindowStart: rowCount === 0 ? 0 : rowOffset + 1,
    showControls:
      showRowControls ||
      showFilteredProjectionPending ||
      showFilteredVisibleCount,
    showFilteredProjectionPending,
    showFilteredVisibleCount,
    showRowControls,
  };
}

export class DatabaseBlockComponent extends CaptionedBlockComponent<DatabaseBlockModel> {
  private _dataViewStickyHeaderElement: HTMLElement | null = null;

  private _dataViewStickyHeaderObserver: ResizeObserver | null = null;

  private _dataViewStickyTopFrame: number | null = null;

  private _databaseRowWindowPagePickerOpen = false;

  private _databaseRowWindowPagePickerAnchor: HTMLElement | null = null;

  private _databaseRowWindowPagePickerCleanup: (() => void) | null = null;

  private _databaseRowWindowPagePickerModel: DatabaseRowWindowPagePickerPortalModel | null =
    null;

  private _databaseRowWindowPagePickerPortal: HTMLDivElement | null = null;

  private _databaseStorageReadyCleanup: (() => void) | null = null;

  /** The server confirmed it holds no root document for this database. */
  private _databaseRootMissing = false;

  private _initialLoadCompleted = false;

  private _missingDataRepair: DatabaseRepairOutcome | 'running' | null = null;

  /** Report of the last restore, completed once the data arrives. */
  private _pendingRestoreReport: {
    reportId: string;
    startedAt: number;
  } | null = null;

  /** Reason of changes the server refused for good; null when none wait. */
  private _refusedChange: DatabaseRefusedChangeReason | null = null;

  private _refusedChangeStep:
    | 'idle'
    | 'confirm-discard'
    | 'running'
    | 'failed' = 'idle';

  private _stopRefusedChangeWatch: (() => void) | null = null;

  /** Stop of the wait for the complete directory the refusal reads again on. */
  private _refusedChangeDirectoryWait: (() => void) | null = null;

  private _databaseRowFocusAwarenessSetup = false;

  private _databaseStorageSubscriptionsSetup = false;

  private _releaseOfflineCopy: (() => void) | null = null;

  private _stopAppUsageWatch: (() => void) | null = null;

  /** The apps that read this database, while the host reports them. */
  protected readonly appUsage$ = signal<DatabaseAppUsageRead | null>(null);

  /** The device's offline copy of this row-sharded database, if offered. */
  protected readonly offlineCopy$ = signal<DatabaseOfflineCopy | null>(null);

  /** Download progress shown above the footer while the copy is pulling. */
  protected readonly offlineCopyFooterProgress$ = computed(() => {
    const progress = this.offlineCopy$.value?.progress$.value;
    if (progress?.status !== 'running') return null;
    const format = new Intl.NumberFormat();
    return {
      label: dataViewI18nT('database.offlineCopy.progress', {
        loaded: format.format(progress.loadedRows),
        total: format.format(progress.totalRows),
      }),
      ratio: progress.totalRows ? progress.loadedRows / progress.totalRows : 0,
    };
  });

  private _fileIndexHighlightIntegrationSetup = false;

  private _fileIndexRowHighlightObserver: MutationObserver | null = null;

  private _fileIndexHighlightedRowIds = new Set<string>();

  private _fileIndexHoveredRowId: string | null = null;

  private _fileIndexHighlightRetry: (() => void) | null = null;

  private _lastLocalRowFocusKey: string | null = null;

  private _lastLocalCellPresenceKey: string | null = null;

  private _lastLocalAreaSelectionPresenceKey: string | null = null;

  private readonly clickDatabaseOps = (
    e: MouseEvent,
    dataViewLogic?: DataViewUILogicBase
  ) => {
    const storageModel = this.dataSource.value.storageModel;
    const popupTarget = popupTargetFromElement(e.currentTarget as HTMLElement);
    const options = this.optionsConfig.configure(this.model, {
      items: [
        menu.input({
          initialValue: storageModel.props.title.toString(),
          placeholder: 'Database title',
          onChange: text => {
            this.dataSource.value.titleSet(text);
          },
        }),
        menu.group({
          items: [
            ...(dataViewLogic
              ? [
                  menu.action({
                    prefix: LayoutIcon(),
                    name: dataViewI18nT('viewSettings.title'),
                    select: () => {
                      popViewOptions(popupTarget, dataViewLogic);
                    },
                  }),
                ]
              : []),
            menu.action({
              prefix: UploadIcon(),
              name: dataViewI18nT('toolbar.importFiles'),
              select: () => {
                openDatabaseBulkUploadPicker(this.blockId);
              },
            }),
            ...this.offlineCopyMenuItems(),
          ],
        }),
        menu.action({
          prefix: CommentIcon(),
          name: 'Comment',
          hide: () => !this.std.getOptional(CommentProviderIdentifier),
          select: () => {
            this.std.getOptional(CommentProviderIdentifier)?.addComment([
              new BlockSelection({
                blockId: this.blockId,
              }),
            ]);
          },
        }),
        menu.action({
          prefix: CopyIcon(),
          name: 'Copy',
          select: () => {
            const slice = Slice.fromModels(this.store, [this.model]);
            this.std.clipboard
              .copySlice(slice)
              .then(() => {
                toast(this.host, 'Copied to clipboard');
              })
              .catch(console.error);
          },
        }),
        menu.action({
          prefix: DownloadIcon(),
          name: 'Export CSV',
          select: () => {
            this.exportDatabaseCsv().catch(error => {
              if (
                error instanceof DOMException &&
                error.name === 'AbortError'
              ) {
                this.notifyDatabaseExport('CSV export cancelled.', 'warning');
                return;
              }
              this.notifyDatabaseExport(
                error instanceof Error ? error.message : String(error),
                'error'
              );
            });
          },
        }),
        menu.group({
          items: [
            menu.action({
              prefix: DeleteIcon(),
              class: {
                'delete-item': true,
              },
              name: 'Delete Database',
              select: () => {
                // Soft-delete through the trash guard so relation
                // dependencies are confirmed before the block disappears.
                // The trash announces itself: the local trash with "moved to
                // trash", a queued server trash with "deletion queued", a
                // refusal through the gate.
                deleteBlocksThroughGate(this.std, [this.model]).catch(error => {
                  console.error(
                    '[DatabaseBlock] Failed to move database to trash:',
                    error
                  );
                  toast(this.host, 'Failed to move database to trash');
                });
              },
            }),
          ],
        }),
      ],
    });

    popMenu(popupTarget, {
      options: {
        ...options,
        preferDesktop: true,
      },
    });
  };

  private notifyDatabaseExport(
    message: string,
    accent: 'info' | 'success' | 'warning' | 'error' = 'info',
    actions?: Parameters<NotificationService['notify']>[0]['actions']
  ) {
    const notification = this.std.getOptional(NotificationProvider);
    if (notification) {
      notification.notify({
        title: 'Database export',
        message,
        accent,
        duration: accent === 'info' ? 0 : 4000,
        actions,
      });
      return;
    }
    toast(this.host, message, accent === 'info' ? 4000 : 2500);
  }

  private readonly exportDatabaseCsv = async () => {
    const abortController = new AbortController();
    let jobId: string | null = null;
    const workspaceId = this.store.workspace.id;
    const docId = this.store.id;
    const databaseId = this.model.id;
    const currentView = this.dataSource.value.viewManager.currentView$.value;
    const currentViewData = currentView?.data$.value as
      | { filter?: unknown; sort?: unknown }
      | undefined;
    const visibleColumnIds =
      currentView?.getBoundedPropertiesWindow().propertyIds ?? [];
    const search = currentView?.search$.value.trim();

    this.notifyDatabaseExport('Preparing CSV export...', 'info', [
      {
        key: 'cancel',
        label: 'Cancel',
        onClick: () => {
          abortController.abort();
          if (jobId) {
            fetch(
              `/api/workspace/${encodeURIComponent(workspaceId)}/database-exports/jobs/${encodeURIComponent(jobId)}/cancel`,
              {
                method: 'POST',
                credentials: 'include',
              }
            ).catch(console.error);
          }
        },
      },
    ]);

    const response = await fetch(
      `/api/workspace/${encodeURIComponent(workspaceId)}/database-exports/docs/${encodeURIComponent(docId)}/databases/${encodeURIComponent(databaseId)}/export`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          format: 'csv',
          columnMode: visibleColumnIds.length > 0 ? 'visible' : 'all',
          columnIds: visibleColumnIds.length > 0 ? visibleColumnIds : undefined,
          search: search || undefined,
          viewFilter: currentViewData?.filter,
          viewId: currentView?.id,
          viewSort: currentViewData?.sort,
        }),
        signal: abortController.signal,
      }
    );

    jobId = response.headers.get('X-Database-Export-Job-Id');
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `CSV export failed (${response.status})`);
    }
    if (!response.body) {
      throw new Error('CSV export response is not streamable');
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let totalBytes = 0;
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      chunks.push(result.value);
      totalBytes += result.value.byteLength;
    }

    const blob = new Blob(
      chunks.map(chunk => {
        const copy = new Uint8Array(chunk.byteLength);
        copy.set(chunk);
        return copy.buffer;
      }),
      { type: 'text/csv;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getDatabaseExportFileName(
      this.dataSource.value.storageModel.props.title.toString()
    );
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);

    this.notifyDatabaseExport(
      `CSV export completed (${Math.ceil(totalBytes / 1024)} KB).`,
      'success'
    );
  };

  protected readonly dataSource = lazy(() => {
    const dataSource = createDatabaseBlockDataSource(this.model, dataSource => {
      dataSource.serviceSet(EditorHostKey, this.host);
      this.std.provider
        .getAll(ExternalGroupByConfigProvider)
        .forEach(config => {
          dataSource.serviceSet(
            ExternalGroupByConfigProvider(config.name),
            config
          );
        });
    });
    if (this.isDatabaseFullscreenSurface) {
      dataSource.setUiStatePersistence('local');
      const baseFeatureFlags$ = dataSource.featureFlags$;
      dataSource.featureFlags$ = computed(() =>
        createCanvasPresentationFeatureFlags(baseFeatureFlags$.value)
      );
    } else {
      dataSource.setUiStatePersistence('durable');
    }
    return dataSource;
  });

  protected disposeDataSource() {
    this._initialLoadCompleted = false;
    this.dataViewRootLogic.clear();
    if (!this.dataSource.hasValue) {
      return;
    }
    this.dataSource.value.dispose();
    this.dataSource.clear();
  }

  private readonly renderTitle = (dataViewLogic: DataViewUILogicBase) => {
    const storageModel = this.dataSource.value.storageModel;
    const isSystemManaged = storageModel.props.systemManaged === true;
    const isMailManaged =
      isSystemManaged &&
      storageModel.props.managedBy === MAIL_MANAGED_DATABASE_OWNER;
    const isSchemaLocked = storageModel.props.schemaLocked === true;
    const schemaLockedTooltip = storageModel.props.managedBy
      ? `Schema locked (${storageModel.props.managedBy}): rows are editable, columns and relation configuration are managed by the system.`
      : 'Schema locked: rows are editable, columns and relation configuration are managed by the system.';
    return html`
      ${this.renderAppUsageBadge()}
      <affine-database-title
        class="${databaseTitleStyles}"
        .titleText="${storageModel.props.title}"
        .dataSource="${this.dataSource.value}"
        .dataViewLogic="${dataViewLogic}"
      ></affine-database-title>
      ${isSystemManaged
        ? html`<span
            class="system-managed-badge"
            title="${isMailManaged
              ? dataViewI18nT('database.managed.mail.hint')
              : storageModel.props.managedBy
                ? `System-managed (${storageModel.props.managedBy})`
                : 'System-managed'}"
            style="
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 2px 8px;
              border-radius: 4px;
              background: var(--streamlain-tag-orange, #fff3e0);
              color: var(--streamlain-v2-text-secondary, #666);
              font-size: 12px;
              line-height: 16px;
              white-space: nowrap;
              user-select: none;
              margin-left: 8px;
            "
            >${isMailManaged
              ? dataViewI18nT('database.managed.mail.label')
              : 'System'}</span
          >`
        : nothing}
      ${isSchemaLocked
        ? html`<span
            class="schema-locked-icon"
            title="${schemaLockedTooltip}"
            aria-label="Schema locked"
            style="
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 4px;
              width: 20px;
              height: 20px;
              border-radius: 4px;
              background: var(--streamlain-v2-layer-background-secondary, #f3f4f6);
              color: var(--streamlain-v2-text-secondary, #666);
              font-size: 14px;
              line-height: 20px;
              white-space: nowrap;
              user-select: none;
            "
            >${LockIcon()}</span
          >`
        : nothing}
    `;
  };

  createTemplate = (
    data: {
      view: SingleView;
      rowId: string;
    },
    openDoc: (docId: string) => void
  ) => {
    return createRecordDetail({
      ...data,
      openDoc,
      detail: {
        header: uniMap(
          createUniComponentFromWebComponent(BlockRenderer),
          props => ({
            ...props,
            host: this.host,
          })
        ),
        note: uniMap(
          createUniComponentFromWebComponent(NoteRenderer),
          props => ({
            ...props,
            model: this.model,
            host: this.host,
          })
        ),
      },
    });
  };

  headerWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) => {
      const chrome = this.databaseSurfaceChromeOptions;
      return renderDatabaseDataViewChrome({
        props,
        surface: chrome.profile,
        inlineBoundary: chrome.showInlineLargeDataBoundary
          ? this.renderInlineLargeDataBoundary()
          : nothing,
        quickSettings: renderUniLit(widgetPresets.quickSettingBar, props),
        showQuickSettings: chrome.showQuickSettings,
        showTitleRow: chrome.showTitleRow,
        showToolbarRow: chrome.showToolbarRow,
        showTools: chrome.showTools,
        showViewBar: chrome.showViewBar,
        sticky: chrome.profile === 'fullscreen',
        titleRow: html` ${this.renderTitle(props.dataViewLogic)} `,
        trailingActions: html`
          ${this.renderDatabaseOps(props.dataViewLogic)}
          ${this.renderFullscreenButton()}
        `,
        tools: renderUniLit(this.toolsWidget, props),
        viewBarOptions: {
          onChangeView: id => {
            window.dispatchEvent(
              new CustomEvent('streamlain:database-view-change', {
                detail: {
                  blockId: this.blockId,
                  viewId: id,
                },
              })
            );
          },
        },
      });
    }
  );

  protected readonly footerWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) => {
      const chrome = this.databaseSurfaceChromeOptions;
      if (
        !chrome.showRowWindowFooter ||
        props.dataViewLogic.isSetupWizardOpen()
      ) {
        return html``;
      }
      const surface = chrome.profile === 'page' ? 'page' : 'fullscreen';
      return this.renderRowDataWindowFooter(props.dataViewLogic.view, {
        surface,
      });
    }
  );

  indicator = new DropIndicator();

  onDrag = (evt: MouseEvent, id: string): (() => void) => {
    const result = getDropResult(evt);
    if (result && result.rect) {
      document.body.append(this.indicator);
      this.indicator.rect = Rect.fromLWTH(
        result.rect.left,
        result.rect.width,
        result.rect.top,
        result.rect.height
      );
      return () => {
        this.indicator.remove();
        const model = this.store.getBlock(id)?.model;
        const target = result.modelState.model;
        let parent = this.store.getParent(target.id);
        const shouldInsertIn = result.placement === 'in';
        if (shouldInsertIn) {
          parent = target;
        }
        if (model && target && parent) {
          if (shouldInsertIn) {
            this.store.moveBlocks([model], parent);
          } else {
            this.store.moveBlocks(
              [model],
              parent,
              target,
              result.placement === 'before'
            );
          }
        }
      };
    }
    this.indicator.remove();
    return () => {};
  };

  private readonly setSelection = (
    selection: DataViewSelection | undefined
  ) => {
    if (selection) {
      getSelection()?.removeAllRanges();
    }
    this.selection.setGroup(
      'note',
      selection
        ? [
            new DatabaseSelection({
              blockId: this.blockId,
              surface: this.databaseSelectionSurface,
              viewSelection: selection,
            }),
          ]
        : []
    );
  };

  protected readonly toolsWidget: DataViewWidget = widgetPresets.createTools({
    table: [
      widgetPresets.tools.filter,
      widgetPresets.tools.sort,
      widgetPresets.tools.search,
    ],
    kanban: [
      widgetPresets.tools.filter,
      widgetPresets.tools.sort,
      widgetPresets.tools.search,
    ],
    gantt: [
      widgetPresets.tools.filter,
      widgetPresets.tools.sort,
      widgetPresets.tools.search,
    ],
    calendar: [
      widgetPresets.tools.filter,
      widgetPresets.tools.sort,
      widgetPresets.tools.search,
    ],
  });

  private readonly viewSelection$ = computed(() => {
    const surface = this.databaseSelectionSurface;
    const databaseSelection = this.selection.value.find(
      (selection): selection is DatabaseSelection =>
        selection instanceof DatabaseSelection &&
        selection.isOn(this.blockId, surface)
    );
    return databaseSelection?.viewSelection;
  });

  protected get rowFocusSelection$(): ReadonlySignal<
    DataViewSelection | undefined
  > {
    return this.viewSelection$;
  }

  protected get rowFocusDataSource() {
    return this.dataSource.value;
  }

  protected get databaseSelectionSurface(): DatabaseSelectionSurface {
    return this.isDatabaseFullscreenSurface ? 'fullscreen' : 'page';
  }

  protected get databaseRowFocusScope() {
    return this.isDatabaseFullscreenSurface
      ? `database:${this.store.id}:${this.blockId}:fullscreen`
      : `database:${this.store.id}:${this.blockId}:page`;
  }

  protected get databaseCellPresenceScope() {
    return `database:${this.store.id}:${this.blockId}:cell`;
  }

  private get databaseSurfaceElement(): HTMLElement | null {
    return this.closest(
      '[data-database-isolated-surface="true"], database-fullscreen-editor, .database-fullscreen-viewport'
    ) as HTMLElement | null;
  }

  private get databaseSurfaceChromeOptions(): DatabaseSurfaceChromeOptions {
    const surface = this.databaseSurfaceElement;
    const profile: DatabaseSurfaceProfile = !surface
      ? 'page'
      : surface.dataset.databaseSurfaceProfile === 'embedded'
        ? 'embedded'
        : 'fullscreen';
    const isEmbedded = profile === 'embedded';

    return {
      forceRowWindowControls: readDatabaseSurfaceBoolean(
        surface,
        'databaseForceRowWindowControls',
        profile === 'fullscreen'
      ),
      profile,
      showInlineLargeDataBoundary: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowInlineLargeDataBoundary',
        !isEmbedded
      ),
      showQuickSettings: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowQuickSettings',
        !isEmbedded
      ),
      showRowWindowFooter: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowRowWindowFooter',
        !isEmbedded
      ),
      showTitleRow: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowTitleRow',
        !isEmbedded
      ),
      showToolbarRow: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowToolbarRow',
        true
      ),
      showTools: readDatabaseSurfaceBoolean(surface, 'databaseShowTools', true),
      showViewBar: readDatabaseSurfaceBoolean(
        surface,
        'databaseShowViewBar',
        !isEmbedded
      ),
    };
  }

  private get isDatabaseFullscreenSurface() {
    return !!this.databaseSurfaceElement;
  }

  private hasActiveFilterOrSearch(view: SingleView) {
    const data = view.data$.value as
      | {
          filter?: {
            conditions?: unknown[];
          };
        }
      | undefined;
    const hasSearch = view.search$.value.trim().length > 0;
    const hasFilter =
      Array.isArray(data?.filter?.conditions) &&
      data.filter.conditions.length > 0;
    return hasSearch || hasFilter;
  }

  protected shouldForceLargeDataWindowControls() {
    return this.databaseSurfaceChromeOptions.forceRowWindowControls;
  }

  protected readonly remoteRowFocuses$ = signal<DataViewRemoteRowFocus[]>([]);

  protected readonly remoteCellPresences$ = signal<
    DataViewRemoteCellPresence[]
  >([]);

  protected readonly remoteAreaSelections$ = signal<
    DataViewRemoteAreaSelectionPresence[]
  >([]);

  private readonly virtualPadding$ = signal(0);

  get optionsConfig(): DatabaseViewExtensionOptions {
    return {
      configure: (_model, options) => options,
      ...this.std.getOptional(DatabaseConfigExtension.identifier),
    };
  }

  get isCommentHighlighted() {
    return (
      this.std
        .getOptional(BlockElementCommentManager)
        ?.isBlockCommentHighlighted(this.model) ?? false
    );
  }

  override get topContenteditableElement() {
    if (this.std.get(DocModeProvider).getEditorMode() === 'edgeless') {
      return this.closest<BlockComponent>(
        EDGELESS_TOP_CONTENTEDITABLE_SELECTOR
      );
    }
    return this.rootComponent;
  }

  protected renderDatabaseOps(dataViewLogic?: DataViewUILogicBase) {
    if (this.dataSource.value.readonly$.value) {
      return nothing;
    }
    return html` <button
      type="button"
      data-testid="database-ops"
      data-canvas-db-zone="toolbar-action"
      class="${databaseOpsStyles}"
      aria-label="Database options"
      title="Database options"
      @click="${(event: MouseEvent) =>
        this.clickDatabaseOps(event, dataViewLogic)}"
    >
      ${MoreHorizontalIcon()}
    </button>`;
  }

  // Streamlain: Render fullscreen button for edgeless canvas databases
  protected renderFullscreenButton() {
    const docMode = this.std.getOptional(DocModeProvider)?.getEditorMode();
    const parent = this.store.getParent(this.model.id);
    if (
      !shouldRenderDatabaseFullscreenButton({
        docMode,
        parentFlavour: parent?.flavour,
        readonly: this.dataSource.value.readonly$.value,
      })
    ) {
      return nothing;
    }
    return html`<button
      type="button"
      data-testid="database-fullscreen"
      data-canvas-db-zone="fullscreen-action"
      class="${databaseFullscreenButtonStyles}"
      title="Fullscreen"
      aria-label="Fullscreen"
      @click="${this.clickFullscreen}"
    >
      ${ExpandFullIcon()}
    </button>`;
  }

  private readonly clickFullscreen = () => {
    const viewId =
      this.dataSource.value.viewManager.currentViewId$.value ?? undefined;
    window.dispatchEvent(
      new CustomEvent('streamlain:database-fullscreen', {
        detail: { databaseId: this.model.id, viewId },
      })
    );
  };

  private readonly extractInlineDatabaseToCanvas = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.dataSource.value.readonly$.value) {
      return;
    }
    const root = this.store.root;
    if (!root) {
      throw new Error('Cannot extract database to canvas without root block');
    }
    this.store.captureSync();
    this.store.updateBlock(this.model, {
      xywh: serializeXYWH(
        0,
        0,
        DEFAULT_CANVAS_DATABASE_WIDTH,
        DEFAULT_CANVAS_DATABASE_HEIGHT
      ),
    });
    this.store.moveBlocks([this.model], root);
    this.std.get(DocModeProvider).setEditorMode('edgeless');
  };

  private getLargeDataWindowContext(view: SingleView) {
    const requestedLimit = view.rowWindowLimit$.value;
    const requestedOffset = view.rowWindowOffset$.value;
    let projection = view.projectRows({
      scope: 'filtered-result',
      window: {
        limit: requestedLimit,
        offset: requestedOffset,
      },
    });
    if (
      projection.totalCount > 0 &&
      projection.window.offset >= projection.totalCount
    ) {
      const clampedOffset =
        Math.floor((projection.totalCount - 1) / requestedLimit) *
        requestedLimit;
      projection = view.projectRows({
        scope: 'filtered-result',
        window: {
          limit: requestedLimit,
          offset: clampedOffset,
        },
      });
    }
    const rowCount = projection.totalCount;
    const rowOffset = projection.window.offset;
    const rowLimit = projection.window.limit;
    const visibleRowCount = projection.rowIds.length;
    const supportsRowPagination = view.type !== 'calendar';

    const state = resolveDatabaseWindowControlsState({
      forceWindowControls:
        supportsRowPagination && this.shouldForceLargeDataWindowControls(),
      hasActiveFilterOrSearch:
        supportsRowPagination && this.hasActiveFilterOrSearch(view),
      projectionComplete: projection.complete,
      projectionTotalKnown: projection.totalKnown,
      rowCount,
      rowLimit,
      rowOffset,
    });

    return {
      projectionComplete: projection.complete,
      projectionSyncState: projection.syncState,
      rowCount,
      rowOffset,
      rowLimit,
      state,
      supportsRowPagination,
      visibleRowCount,
    };
  }

  private getWindowLimitOptions({
    current,
    locale,
    max,
    presets,
  }: {
    current: number;
    locale: DatabaseWindowControlsLocale;
    max: number;
    presets: readonly number[];
  }) {
    return [...new Set([...presets, current])]
      .filter(value => value > 0 && value <= max)
      .sort((a, b) => a - b)
      .map(value => ({
        label: formatDatabaseWindowPageSizeLabel({
          locale,
          rowLimit: value,
        }),
        value,
      }));
  }

  private getFooterSelectionForView(view: SingleView): DatabaseFooterSelection {
    const selection = this.viewSelection$.value as
      | (DataViewSelection & {
          cards?: Array<{ cardId?: string }>;
          rowId?: string;
          rows?: Array<unknown>;
          selectionType?: string;
          viewId?: string;
        })
      | undefined;

    if (!selection || selection.viewId !== view.id) {
      return { canDeleteRows: false, rowIds: [] };
    }

    if (selection.selectionType === 'row') {
      const rowIds = Array.isArray(selection.rows)
        ? selection.rows
            .map(row => (row as { id?: unknown }).id)
            .filter((id): id is string => typeof id === 'string')
        : typeof selection.rowId === 'string'
          ? [selection.rowId]
          : [];
      return { canDeleteRows: rowIds.length > 0, rowIds };
    }

    if (selection.selectionType === 'bar') {
      return {
        canDeleteRows: false,
        rowIds: typeof selection.rowId === 'string' ? [selection.rowId] : [],
      };
    }

    if (selection.selectionType === 'card' && Array.isArray(selection.cards)) {
      const rowIds = [
        ...new Set(
          selection.cards
            .map(card => card.cardId)
            .filter((cardId): cardId is string => typeof cardId === 'string')
        ),
      ];
      return { canDeleteRows: rowIds.length > 0, rowIds };
    }

    return { canDeleteRows: false, rowIds: [] };
  }

  private clearFooterSelection() {
    this.setSelection(undefined);
    this.requestDataViewFrameUpdate();
  }

  private deleteFooterSelectedRows(
    view: SingleView,
    rowIds: readonly string[]
  ) {
    if (rowIds.length === 0) {
      return;
    }

    const run = () => {
      view.rowsDelete([...rowIds]);
      this.clearFooterSelection();
    };
    const confirm = (
      window as Window & {
        __streamlainHooks?: {
          confirmDeleteRows?: (count: number) => Promise<boolean>;
        };
      }
    ).__streamlainHooks?.confirmDeleteRows;
    if (confirm && rowIds.length > 1) {
      void confirm(rowIds.length).then(ok => {
        if (ok) run();
      });
      return;
    }
    run();
  }

  private closeDatabaseRowWindowPagePicker() {
    if (!this._databaseRowWindowPagePickerOpen) {
      return;
    }

    this._databaseRowWindowPagePickerOpen = false;
    this._databaseRowWindowPagePickerModel = null;
    this.teardownDatabaseRowWindowPagePickerPortal();
    this.requestUpdate();
    this.requestDataViewFrameUpdate();
  }

  protected requestDataViewFrameUpdate() {
    this.dataViewRootLogic.value.dataViewRenderer?.requestUpdate();
  }

  private ensureDatabaseRowWindowPagePickerPortal() {
    if (typeof document === 'undefined') {
      return null;
    }

    if (!this._databaseRowWindowPagePickerPortal) {
      const portal = document.createElement('div');
      portal.className = databaseRowWindowPageMenuPortalStyles;
      portal.dataset.streamlainDatabaseWindowPagePicker = 'true';
      document.body.append(portal);
      this._databaseRowWindowPagePickerPortal = portal;
    }

    return this._databaseRowWindowPagePickerPortal;
  }

  private getDatabaseRowWindowPagePickerAnchor() {
    const currentAnchor = this.renderRoot.querySelector<HTMLElement>(
      '[data-database-window-page-picker-anchor="true"]'
    );
    if (currentAnchor) {
      this._databaseRowWindowPagePickerAnchor = currentAnchor;
      return currentAnchor;
    }

    return this._databaseRowWindowPagePickerAnchor?.isConnected
      ? this._databaseRowWindowPagePickerAnchor
      : null;
  }

  private renderDatabaseRowWindowPagePickerOptions(
    model: DatabaseRowWindowPagePickerPortalModel
  ) {
    return repeat(
      Array.from({ length: model.pageCount }, (_, index) => index + 1),
      page => page,
      page =>
        html`<button
          type="button"
          class="database-window-page-option"
          role="option"
          aria-selected="${page === model.currentPage}"
          data-selected="${page === model.currentPage}"
          @mousedown="${(event: MouseEvent) => event.preventDefault()}"
          @click="${() => this.selectDatabaseRowWindowPage(page)}"
        >
          ${formatDatabaseWindowNumber(page, model.locale)}
        </button>`
    );
  }

  private selectDatabaseRowWindowPage(page: number) {
    const model = this._databaseRowWindowPagePickerModel;
    if (!model) {
      this.closeDatabaseRowWindowPagePicker();
      return;
    }

    model.view.setRowWindowOffset((page - 1) * model.rowLimit);
    this.closeDatabaseRowWindowPagePicker();
  }

  private syncDatabaseRowWindowPagePickerPortal() {
    const model = this._databaseRowWindowPagePickerModel;
    if (!this._databaseRowWindowPagePickerOpen || !model) {
      this.teardownDatabaseRowWindowPagePickerPortal();
      return;
    }

    const anchor = this.getDatabaseRowWindowPagePickerAnchor();
    const portal = this.ensureDatabaseRowWindowPagePickerPortal();
    if (!anchor || !portal) {
      this.teardownDatabaseRowWindowPagePickerPortal();
      return;
    }

    portal.className = databaseRowWindowPageMenuPortalStyles;
    portal.setAttribute('role', 'listbox');
    portal.setAttribute('aria-label', model.selectPageLabel);
    render(this.renderDatabaseRowWindowPagePickerOptions(model), portal);

    this._databaseRowWindowPagePickerCleanup?.();
    this._databaseRowWindowPagePickerCleanup = autoUpdate(
      anchor,
      portal,
      () => {
        void computePosition(anchor, portal, {
          middleware: [
            offset(4),
            size({
              padding: 8,
              apply({ availableHeight, rects }) {
                portal.style.maxHeight = `${Math.max(
                  48,
                  Math.min(160, Math.floor(availableHeight))
                )}px`;
                portal.style.minWidth = `${Math.max(
                  80,
                  Math.ceil(rects.reference.width)
                )}px`;
              },
            }),
            shift({ padding: 8 }),
          ],
          placement: 'top-end',
          strategy: 'fixed',
        }).then(({ x, y }) => {
          portal.style.left = `${x}px`;
          portal.style.top = `${y}px`;
        });
      }
    );
  }

  private teardownDatabaseRowWindowPagePickerPortal() {
    this._databaseRowWindowPagePickerCleanup?.();
    this._databaseRowWindowPagePickerCleanup = null;

    if (!this._databaseRowWindowPagePickerPortal) {
      return;
    }

    render(nothing, this._databaseRowWindowPagePickerPortal);
    this._databaseRowWindowPagePickerPortal.remove();
    this._databaseRowWindowPagePickerPortal = null;
  }

  protected renderRowDataWindowFooter(
    view: SingleView,
    options: { surface?: 'canvas' | 'page' | 'fullscreen' } = {}
  ) {
    const {
      projectionComplete,
      projectionSyncState,
      rowCount,
      rowLimit,
      rowOffset,
      state,
      supportsRowPagination,
      visibleRowCount,
    } = this.getLargeDataWindowContext(view);

    const moveRows = (delta: number) => {
      view.setRowWindowOffset(rowOffset + delta);
      this.requestDataViewFrameUpdate();
    };
    const locale = getRuntimeDatabaseWindowControlsLocale();
    const copy = DATABASE_WINDOW_CONTROL_COPY[locale];
    const rowLimitOptions = this.getWindowLimitOptions({
      current: rowLimit,
      locale,
      max: DATABASE_LARGE_DATA_POLICY.ui.maxPageSize,
      presets: DATABASE_ROW_WINDOW_LIMIT_PRESETS,
    });
    const projectionStatusLabel = resolveDatabaseWindowProjectionStatusLabel({
      locale,
      projectionComplete,
      projectionSyncState,
    });
    const rowStatusLabel =
      projectionSyncState === 'unsupported'
        ? copy.updateFailed
        : formatDatabaseWindowRowsLabel({
            locale,
            rowCount,
            state,
            viewMode: view.type,
          });
    const footerSelection = this.getFooterSelectionForView(view);
    const selectedRowIds = footerSelection.rowIds;
    const selectedRowCount = selectedRowIds.length;
    const canDeleteSelectedRows =
      footerSelection.canDeleteRows &&
      selectedRowCount > 0 &&
      !this.store.readonly;
    const selectionToolbarActive =
      selectedRowCount > 0 && !projectionStatusLabel;
    const selectedRowsLabel = `${formatDatabaseWindowNumber(
      selectedRowCount,
      locale
    )} ${copy.selectedRows}`;
    const selectedRowDigitCount = Math.max(1, String(selectedRowCount).length);
    const selectedControlPillWidth = Math.max(
      canDeleteSelectedRows ? 188 : 154,
      124 + selectedRowDigitCount * 8 + (canDeleteSelectedRows ? 36 : 0)
    );
    const pageControlPillWidth = locale === 'de' ? 242 : 226;
    const controlPillWidth = selectionToolbarActive
      ? selectedControlPillWidth
      : pageControlPillWidth;
    const showPaginationControls =
      supportsRowPagination && state.showRowControls;
    const showCenterPill = showPaginationControls || selectionToolbarActive;
    const pageControlActive = state.showRowControls;
    const pageNavigationActive = pageControlActive && state.pageCount > 1;
    const limitControlActive =
      state.showRowControls && rowLimitOptions.length > 1;
    if (!pageControlActive) {
      this._databaseRowWindowPagePickerOpen = false;
    }
    this._databaseRowWindowPagePickerModel =
      this._databaseRowWindowPagePickerOpen && pageControlActive
        ? {
            currentPage: state.currentPage,
            locale,
            pageCount: state.pageCount,
            rowLimit,
            selectPageLabel: copy.selectPage,
            view,
          }
        : null;
    const setRowLimit = (limit: number) => {
      view.setRowWindowLimit(limit);
      this.requestDataViewFrameUpdate();
    };
    const openRowLimitMenu = (event: MouseEvent) => {
      if (!limitControlActive) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      popMenu(popupTargetFromElement(event.currentTarget as HTMLElement), {
        options: {
          items: rowLimitOptions.map(option =>
            menu.action({
              isSelected: option.value === rowLimit,
              name: option.label,
              select: () => setRowLimit(option.value),
            })
          ),
        },
      });
    };
    const setRowPage = (event: Event) => {
      const input = event.currentTarget as HTMLInputElement;
      const page = Math.min(
        state.pageCount,
        Math.max(1, Math.trunc(Number(input.value) || state.currentPage))
      );
      input.value = String(page);
      view.setRowWindowOffset((page - 1) * rowLimit);
      this.requestDataViewFrameUpdate();
      this.closeDatabaseRowWindowPagePicker();
    };
    const openPagePicker = (event?: Event) => {
      if (!pageControlActive) {
        return;
      }
      const target = event?.currentTarget;
      if (target instanceof HTMLElement) {
        const anchor = target.closest<HTMLElement>(
          '.database-window-page-combobox'
        );
        if (anchor) {
          this._databaseRowWindowPagePickerAnchor = anchor;
        }
      }
      this._databaseRowWindowPagePickerOpen = true;
      this.requestUpdate();
      this.requestDataViewFrameUpdate();
    };
    const closePagePicker = () => this.closeDatabaseRowWindowPagePicker();
    const handlePagePickerFocusOut = (event: FocusEvent) => {
      const nextTarget = event.relatedTarget as Node | null;
      const currentTarget = event.currentTarget as HTMLElement;
      if (!nextTarget || !currentTarget.contains(nextTarget)) {
        closePagePicker();
      }
    };
    const handlePageKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        setRowPage(event);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openPagePicker();
        return;
      }
      if (event.key === 'Escape') {
        const input = event.currentTarget as HTMLInputElement;
        input.value = String(state.currentPage);
        input.blur();
        closePagePicker();
      }
    };
    const footerClass = [
      databaseRowWindowFooterStyles,
      options.surface === 'canvas'
        ? databaseCanvasRowWindowFooterStyles
        : this.isDatabaseFullscreenSurface || options.surface === 'fullscreen'
          ? databaseFullscreenRowWindowFooterStyles
          : databaseStickyRowWindowFooterStyles,
    ]
      .filter(Boolean)
      .join(' ');

    return html`<div
      data-testid="database-row-window-footer"
      data-controls-visible=${state.showControls ? 'true' : 'false'}
      data-page-active=${pageControlActive}
      data-limit-active=${limitControlActive}
      data-selection-active=${selectionToolbarActive ? 'true' : 'false'}
      data-expanded=${this._databaseRowWindowPagePickerOpen}
      class="${footerClass}"
    >
      <database-projection-status-overlay
        .label=${copy.updating}
        .visible=${!projectionComplete && projectionSyncState !== 'unsupported'}
      ></database-projection-status-overlay>
      <span class="database-window-section">
        <span class="database-window-summary" title="${copy.summaryHint}">
          <span class="database-window-status" aria-live="polite">
            ${rowStatusLabel}
          </span>
          ${state.showFilteredVisibleCount && !projectionStatusLabel
            ? html`<span
                class="database-window-meta"
                data-testid="database-window-filtered-visible"
              >
                ${formatDatabaseWindowNumber(visibleRowCount, locale)}
                ${copy.visible}
              </span>`
            : nothing}
          ${state.showFilteredProjectionPending && !projectionStatusLabel
            ? html`<span
                class="database-window-meta"
                data-testid="database-window-filtered-pending"
              >
                ${copy.filteredPending}
              </span>`
            : nothing}
        </span>
        ${showCenterPill
          ? html`<span
              class="database-window-center"
              title="${selectionToolbarActive
                ? selectedRowsLabel
                : copy.navHint}"
            >
              <span
                class="database-window-control-pill"
                data-selection-active=${selectionToolbarActive
                  ? 'true'
                  : 'false'}
                style="--database-window-control-pill-width: ${controlPillWidth}px"
              >
                <span class="database-window-control-layer">
                  ${showPaginationControls
                    ? html`<span
                        class="database-window-nav"
                        data-inactive=${!pageControlActive}
                      >
                        <button
                          type="button"
                          class="database-window-icon-button"
                          data-testid="database-row-window-prev"
                          aria-label="${copy.previousPage}"
                          title="${copy.previousPage}"
                          ?disabled="${!pageNavigationActive ||
                          rowOffset === 0}"
                          @click="${() => moveRows(-rowLimit)}"
                        >
                          ${ArrowLeftSmallIcon({
                            width: '16px',
                            height: '16px',
                          })}
                        </button>
                        <label class="database-window-page">
                          <span>${copy.page}</span>
                          <span
                            class="database-window-page-combobox"
                            data-database-window-page-picker-anchor="true"
                            @focusout="${handlePagePickerFocusOut}"
                          >
                            <input
                              class="database-window-page-input"
                              type="number"
                              min="1"
                              max="${state.pageCount}"
                              step="1"
                              aria-label="${copy.selectPage}"
                              aria-expanded="${this
                                ._databaseRowWindowPagePickerOpen}"
                              aria-haspopup="listbox"
                              title="${copy.selectPage}"
                              data-testid="database-row-window-page"
                              .value="${String(state.currentPage)}"
                              ?disabled="${!pageControlActive}"
                              @change="${setRowPage}"
                              @focus="${openPagePicker}"
                              @click="${openPagePicker}"
                              @keydown="${handlePageKeydown}"
                            />
                          </span>
                          <span>
                            ${copy.of}
                            ${formatDatabaseWindowNumber(
                              state.pageCount,
                              locale
                            )}
                          </span>
                        </label>
                        <button
                          type="button"
                          class="database-window-icon-button"
                          data-testid="database-row-window-next"
                          aria-label="${copy.nextPage}"
                          title="${copy.nextPage}"
                          ?disabled="${!pageNavigationActive ||
                          state.rowWindowEnd >= rowCount}"
                          @click="${() => moveRows(rowLimit)}"
                        >
                          ${ArrowRightSmallIcon({
                            width: '16px',
                            height: '16px',
                          })}
                        </button>
                      </span>`
                    : nothing}
                </span>
                <span
                  class="database-window-selection-layer"
                  aria-hidden=${selectionToolbarActive ? 'false' : 'true'}
                >
                  <span
                    class="database-window-selection-toolbar"
                    data-doc-explorer-selection-ui="true"
                  >
                    <span class="database-window-selection-count">
                      ${selectedRowsLabel}
                    </span>
                    <button
                      type="button"
                      class="database-window-selection-button"
                      data-testid="database-row-window-clear-selection"
                      aria-label="${copy.clearSelection}"
                      title="${copy.clearSelection}"
                      @click="${() => this.clearFooterSelection()}"
                    >
                      ${CloseIcon({ width: '14px', height: '14px' })}
                    </button>
                    ${canDeleteSelectedRows
                      ? html`<span
                            class="database-window-selection-separator"
                            aria-hidden="true"
                          ></span>
                          <button
                            type="button"
                            class="database-window-selection-button database-window-selection-button-danger"
                            data-testid="database-row-window-delete-selection"
                            aria-label="${copy.deleteSelection}"
                            title="${copy.deleteSelection}"
                            @click="${() =>
                              this.deleteFooterSelectedRows(
                                view,
                                selectedRowIds
                              )}"
                          >
                            ${DeleteIcon({ width: '14px', height: '14px' })}
                          </button>`
                      : nothing}
                  </span>
                </span>
              </span>
            </span>`
          : html`<span></span>`}
        ${showPaginationControls
          ? html`<span
              class="database-window-actions"
              title="${copy.actionsHint}"
            >
              <label
                class="database-window-limit"
                data-inactive=${!limitControlActive}
              >
                <button
                  type="button"
                  class="database-window-limit-button"
                  data-testid="database-row-window-limit"
                  aria-haspopup="menu"
                  aria-label="${copy.actionsHint}"
                  ?disabled="${!limitControlActive}"
                  @click="${openRowLimitMenu}"
                >
                  <span>
                    ${formatDatabaseWindowPageSizeLabel({
                      locale,
                      rowLimit,
                    })}
                  </span>
                </button>
              </label>
            </span>`
          : html`<span></span>`}
      </span>
    </div>`;
  }

  protected renderInlineLargeDataBoundary() {
    const docMode = this.std.getOptional(DocModeProvider)?.getEditorMode();
    const parent = this.store.getParent(this.model.id);
    const rowCount = this.dataSource.value.rowCount$.value;
    const columnCount = this.dataSource.value.columnCount$.value;
    if (
      !shouldRenderDatabaseInlineLargeDataPreview({
        docMode,
        isFullscreen: this.isDatabaseFullscreenSurface,
        parentFlavour: parent?.flavour,
        columnCount,
        rowCount,
      })
    ) {
      return nothing;
    }
    return html`<div
      data-testid="database-inline-large-data-boundary"
      class="${databaseInlineLargeDataBoundaryStyles}"
      title="Large database inline window"
    >
      <span
        >Rows ${Math.min(rowCount, DATA_SOURCE_UI_WINDOW_MAX_LIMIT)}/${rowCount}
        · Columns
        ${Math.min(
          columnCount,
          DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
        )}/${columnCount}</span
      >
      ${this.dataSource.value.readonly$.value
        ? nothing
        : html`<button
            type="button"
            data-testid="database-inline-large-data-extract"
            @click="${this.extractInlineDatabaseToCanvas}"
          >
            Move to canvas
          </button>`}
    </div>`;
  }

  protected renderInlineLargeDataPreview() {
    const docMode = this.std.getOptional(DocModeProvider)?.getEditorMode();
    const parent = this.store.getParent(this.model.id);
    const rowCount = this.dataSource.value.rowCount$.value;
    const columnCount = this.dataSource.value.columnCount$.value;
    if (
      !shouldRenderDatabaseInlineLargeDataPreview({
        docMode,
        isFullscreen: this.isDatabaseFullscreenSurface,
        parentFlavour: parent?.flavour,
        columnCount,
        rowCount,
      })
    ) {
      return nothing;
    }

    const title =
      this.dataSource.value.storageModel.props.title.toString().trim() ||
      'Untitled';
    return html`<div
      data-testid="database-inline-large-data-preview"
      class="${databaseInlineLargeDataPreviewStyles}"
    >
      <div class="database-inline-large-data-preview-title">${title}</div>
      <div class="database-inline-large-data-preview-message">
        Large database preview is bounded in notes and pages.
      </div>
      ${this.renderInlineLargeDataBoundary()}
    </div>`;
  }

  /** Brings a row into view in the view this block shows (a crewmate's playback while followed). */
  revealAgentRow(rowId: string): boolean {
    return (
      this.dataViewRootLogic.value.currentView$.value?.revealRow(rowId) ?? false
    );
  }

  override connectedCallback() {
    super.connectedCallback();

    this.setAttribute(RANGE_SYNC_EXCLUDE_ATTR, 'true');
    this.classList.add(databaseBlockStyles);
    this.syncDatabaseSurfaceAttribute();
    this.syncDatabaseInitialLoadReadyAttribute(false);
    // A queued trash hides the table or shows its phase until the receipt.
    this.disposables.add(
      subscribeBlockPendingDeletion(this.model, () =>
        this.onDatabasePendingDeletionChange()
      )
    );
    // Only the editor that cut the database shows it waiting for its paste.
    this.disposables.add(
      effect(() => {
        this.toggleAttribute(
          'data-database-cut-pending',
          isDatabaseCutPending(this.store.id, this.model.id)
        );
      })
    );

    if (this.syncDatabaseVisibilityDisplay()) {
      this.syncDatabaseInitialLoadReadyAttribute(true);
      return;
    }

    if (this.isDatabaseFullscreenSurface) {
      this.style.display = 'flex';
    }

    this.syncDatabaseUiStatePersistence();
    this.listenFullWidthChange();
    this.ensureDatabaseRowFocusAwareness();
    this.ensureDatabaseStorageSubscriptions();
    this.ensureFileIndexHighlightIntegration();
    this.watchRefusedChanges();
    this.disposables.add(() => this.teardownDataViewStickyTopSync());
    this.disposables.add(() => this.teardownDatabaseStorageReadyObserver());
  }

  override disconnectedCallback() {
    this._databaseRowWindowPagePickerOpen = false;
    this._databaseRowWindowPagePickerModel = null;
    this.teardownDatabaseRowWindowPagePickerPortal();
    this.clearFileIndexRowHighlights();
    this._fileIndexHighlightRetry?.();
    this._fileIndexHighlightRetry = null;
    this._fileIndexRowHighlightObserver?.disconnect();
    this._fileIndexRowHighlightObserver = null;
    this.teardownDatabaseStorageReadyObserver();
    this._stopRefusedChangeWatch?.();
    this._stopRefusedChangeWatch = null;
    this._refusedChangeDirectoryWait?.();
    this._refusedChangeDirectoryWait = null;
    this._releaseOfflineCopy?.();
    this._releaseOfflineCopy = null;
    this.offlineCopy$.value = null;
    this._stopAppUsageWatch?.();
    this._stopAppUsageWatch = null;
    this.appUsage$.value = null;
    this.disposeDataSource();
    super.disconnectedCallback();
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (this.syncDatabaseVisibilityDisplay()) {
      this.syncDatabaseInitialLoadReadyAttribute(true);
      this._databaseRowWindowPagePickerOpen = false;
      this._databaseRowWindowPagePickerModel = null;
      this.teardownDatabaseRowWindowPagePickerPortal();
      return;
    }
    this.syncDatabaseSurfaceAttribute();
    this.syncDatabaseInitialLoadReadyAttribute();
    this.syncDatabaseUiStatePersistence();
    this.scheduleDataViewStickyTopSync();
    this.syncDatabaseRowWindowPagePickerPortal();
  }

  protected onDatabasePendingDeletionChange() {
    this.requestUpdate();
  }

  private syncDatabaseVisibilityDisplay(): boolean {
    if (isHiddenFileIndexDatabase(this.model)) {
      this.style.display = 'none';
      return true;
    }

    // Streamlain: Hide canvas-only databases in page mode. `updated()` runs
    // after initial connect and must preserve the same visibility contract.
    if (!this.isDatabaseFullscreenSurface && !this.shouldRenderInPageMode()) {
      this.style.display = 'none';
      return true;
    }

    // Hidden only while its trash is queued: the block stays set up, so a
    // cancel shows it at once.
    if (blockPendingDeletionState(this.model).kind === 'hidden') {
      this.style.display = 'none';
      return false;
    }

    if (this.isDatabaseFullscreenSurface) {
      this.style.display = 'flex';
      return false;
    }

    if (this.style.display === 'none') {
      this.style.display = '';
    }

    return false;
  }

  private syncDatabaseSurfaceAttribute() {
    this.dataset.streamlainDatabaseSurface = this.isDatabaseFullscreenSurface
      ? 'fullscreen'
      : 'page';
  }

  private getDatabaseInitialLoadReadiness(): DatabaseRowShardReadiness {
    const readiness = resolveDatabaseBodyReadiness(
      this.readDatabaseRowWindowReadiness(),
      this._initialLoadCompleted
    );
    if (readiness.status === 'ready') this._initialLoadCompleted = true;
    return readiness;
  }

  private readDatabaseRowWindowReadiness(): DatabaseRowShardReadiness {
    if (!this.isDatabaseStorageReady()) {
      return this._databaseRootMissing
        ? {
            message: dataViewI18nT('database.dataMissing.description'),
            missing: true,
            repairable: false,
            status: 'error',
          }
        : { status: 'loading' };
    }
    return this.dataSource.value.getInitialRowWindowReadiness();
  }

  private isDatabaseInitialLoadReady() {
    return this.getDatabaseInitialLoadReadiness().status === 'ready';
  }

  protected createMissingDataRepairRequest(
    missing: DatabaseMissingStorage,
    rowShardDocIds: readonly string[]
  ): DatabaseRepairRequest | null {
    const { databaseId, databaseRootDocId } = this.model.props;
    if (!databaseId) return null;
    return {
      blockId: this.model.id,
      databaseId,
      workspaceId: this.model.store.workspace.id,
      missing,
      docIds: [
        ...new Set(
          [databaseRootDocId, ...rowShardDocIds].filter(
            (docId): docId is string => !!docId
          )
        ),
      ],
    };
  }

  /**
   * Called when the database renders its data: a restore that was waiting for
   * it completes its admin report with the time until the data arrived.
   */
  protected confirmRestoredDataArrived() {
    const pending = this._pendingRestoreReport;
    if (!pending) return;
    this._pendingRestoreReport = null;
    this._missingDataRepair = null;
    this.optionsConfig.repairActions?.confirmRestored(
      pending.reportId,
      Date.now() - pending.startedAt
    );
  }

  /**
   * Restore and report actions for a database whose data is missing. Restore
   * retries the stuck documents now instead of waiting out the self-healing
   * backoff; the outcome stays visible until the data arrives.
   */
  protected renderMissingDataActions(
    request: DatabaseRepairRequest | null,
    classes: {
      actions: string;
      button: string;
      primaryButton: string;
      status: string;
    }
  ) {
    const actions = this.optionsConfig.repairActions;
    if (!actions || !request) return nothing;
    const state = this._missingDataRepair;
    const restore = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (this._missingDataRepair === 'running') return;
      this._missingDataRepair = 'running';
      this.requestUpdate();
      const startedAt = Date.now();
      void actions
        .restore(request)
        .catch(() => ({ outcome: 'failed' as const, reportId: null }))
        .then(({ outcome, reportId }) => {
          this._missingDataRepair = outcome;
          this._pendingRestoreReport = reportId
            ? { reportId, startedAt }
            : null;
          this.requestUpdate();
        });
    };
    const report = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      actions.reportBug(request);
    };
    return html`
      <div class=${classes.actions}>
        ${repairActionButton({
          action: 'database.restore',
          className: classes.primaryButton,
          disabled: state === 'running',
          label:
            state === 'running'
              ? dataViewI18nT('database.dataMissing.restoring')
              : dataViewI18nT('database.dataMissing.restore'),
          onClick: restore,
          testId: 'database-data-restore',
        })}
        ${repairActionButton({
          action: 'database.report',
          className: classes.button,
          label: dataViewI18nT('database.dataMissing.report'),
          onClick: report,
          testId: 'database-data-report',
        })}
      </div>
      ${state && state !== 'running'
        ? html`<div
            class=${classes.status}
            role="status"
            data-outcome=${state}
            data-testid="database-data-restore-outcome"
          >
            ${dataViewI18nT(`database.dataMissing.outcome.${state}`)}
          </div>`
        : nothing}
    `;
  }

  /**
   * The documents of the database a refusal may sit on. Resending or deleting
   * (`complete`) needs every shard document and has no request until the
   * complete directory is there. The watch reads the root document at once
   * and reads again once the directory settles.
   */
  protected createRefusedChangeRequest(
    scope: 'watch' | 'complete'
  ): DatabaseRefusedChangeRequest | null {
    const { databaseId, databaseRootDocId } = this.model.props;
    if (!databaseId) return null;
    const directory = this.isDatabaseStorageReady()
      ? this.dataSource.value.getCompleteShardDirectory()
      : undefined;
    if (directory && directory.status !== 'ready') {
      if (scope === 'complete') return null;
      if (directory.status === 'loading')
        this.rereadRefusedChangesOnceDirectorySettles();
    }
    const manifest =
      directory?.status === 'ready' ? directory.manifest : undefined;
    return {
      blockId: this.model.id,
      databaseId,
      workspaceId: this.model.store.workspace.id,
      docIds: [
        ...new Set(
          [
            databaseRootDocId,
            ...(manifest?.shards.map(shard => shard.serverDocId) ?? []),
          ].filter((docId): docId is string => !!docId)
        ),
      ],
    };
  }

  private watchRefusedChanges() {
    const actions = this.optionsConfig.refusedChangeActions;
    if (!actions || this._stopRefusedChangeWatch) return;
    this._stopRefusedChangeWatch = actions.watch(
      () => this.createRefusedChangeRequest('watch'),
      reason => {
        if (reason === this._refusedChange) return;
        this._refusedChange = reason;
        this._refusedChangeStep = 'idle';
        this.requestUpdate();
      }
    );
  }

  /**
   * The first read can run before the storage model is there; it then knows
   * only the root document and misses a refusal on a row shard. Read again
   * once the shard list is known.
   */
  private rereadRefusedChanges() {
    if (!this._stopRefusedChangeWatch) return;
    this._stopRefusedChangeWatch();
    this._stopRefusedChangeWatch = null;
    this.watchRefusedChanges();
  }

  private rereadRefusedChangesOnceDirectorySettles() {
    if (this._refusedChangeDirectoryWait) return;
    this._refusedChangeDirectoryWait = onShardDirectoryComplete(
      this.dataSource.value,
      () => {
        this._refusedChangeDirectoryWait = null;
        this.rereadRefusedChanges();
        // The actions need the complete directory; they turn usable now.
        this.requestUpdate();
      }
    );
  }

  /**
   * Changes the server refused for good stay on this device. The notice names
   * the reason and lets the user send them again or delete them; the data
   * below stays visible so the changes can be read before deciding.
   */
  protected renderRefusedChangesNotice(classes: {
    notice: string;
    title: string;
    message: string;
    actions: string;
    button: string;
    primaryButton: string;
    status: string;
  }) {
    const actions = this.optionsConfig.refusedChangeActions;
    const reason = this._refusedChange;
    if (!actions || !reason) return nothing;
    const step = this._refusedChangeStep;
    const setStep = (next: typeof step) => {
      this._refusedChangeStep = next;
      this.requestUpdate();
    };
    const run = (action: 'resend' | 'discard') => (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const request = this.createRefusedChangeRequest('complete');
      if (!request || this._refusedChangeStep === 'running') return;
      setStep('running');
      actions[action](request).then(
        () => {
          // Queued again: the watch reports a new refusal, should one follow.
          this._refusedChange = null;
          setStep('idle');
        },
        () => setStep('failed')
      );
    };
    const ask = (next: typeof step) => (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setStep(next);
    };
    const running = step === 'running';
    // Resend and delete act on every shard document of the database.
    const unavailable =
      running || this.createRefusedChangeRequest('complete') === null;
    return html`
      <div
        class=${classes.notice}
        contenteditable="false"
        role="alert"
        data-refused-reason=${reason}
        data-testid="database-refused-changes"
      >
        <div class=${classes.title}>
          ${dataViewI18nT('database.refusedChanges.title')}
        </div>
        <div class=${classes.message}>
          ${dataViewI18nT(`database.refusedChanges.reason.${reason}`)}
          ${dataViewI18nT('database.refusedChanges.description')}
        </div>
        ${step === 'confirm-discard'
          ? html`
              <div class=${classes.message}>
                ${dataViewI18nT('database.refusedChanges.discardConfirm')}
              </div>
              <div class=${classes.actions}>
                ${repairActionButton({
                  action: 'database.refused-changes.discard',
                  className: classes.primaryButton,
                  label: dataViewI18nT(
                    'database.refusedChanges.discardConfirmAction'
                  ),
                  onClick: run('discard'),
                  testId: 'database-refused-changes-discard-confirm',
                })}
                ${repairActionButton({
                  action: 'database.refused-changes.keep',
                  className: classes.button,
                  label: dataViewI18nT('database.refusedChanges.cancel'),
                  onClick: ask('idle'),
                  testId: 'database-refused-changes-discard-cancel',
                })}
              </div>
            `
          : html`
              <div class=${classes.actions}>
                ${repairActionButton({
                  action: 'database.refused-changes.resend',
                  className: classes.primaryButton,
                  disabled: unavailable,
                  label: running
                    ? dataViewI18nT('database.refusedChanges.working')
                    : dataViewI18nT('database.refusedChanges.resend'),
                  onClick: run('resend'),
                  testId: 'database-refused-changes-resend',
                })}
                ${repairActionButton({
                  action: 'database.refused-changes.discard-ask',
                  className: classes.button,
                  disabled: unavailable,
                  label: dataViewI18nT('database.refusedChanges.discard'),
                  onClick: ask('confirm-discard'),
                  testId: 'database-refused-changes-discard',
                })}
              </div>
            `}
        ${step === 'failed'
          ? html`<div
              class=${classes.status}
              role="status"
              data-outcome="failed"
              data-testid="database-refused-changes-failed"
            >
              ${dataViewI18nT('database.refusedChanges.failed')}
            </div>`
          : nothing}
      </div>
    `;
  }

  protected readonly repairRowShardManifestDrift = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void this.dataSource.value.repairLoadedRowShardManifestDrift().then(
      () => {
        toast(this.host, dataViewI18nT('database.shardRepair.success'));
        this.requestUpdate();
      },
      (error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : dataViewI18nT('database.shardRepair.failed');
        toast(this.host, message);
      }
    );
  };

  private syncDatabaseInitialLoadReadyAttribute(
    ready = this.isDatabaseInitialLoadReady()
  ) {
    this.setAttribute(
      DATABASE_INITIAL_LOAD_READY_ATTR,
      ready ? 'true' : 'false'
    );
  }

  private syncDatabaseUiStatePersistence() {
    if (!this.isConnected) {
      return;
    }
    if (!this.isDatabaseStorageReady()) {
      return;
    }
    this.dataSource.value.setUiStatePersistence(
      this.isDatabaseFullscreenSurface ? 'local' : 'durable'
    );
  }

  private scheduleDataViewStickyTopSync() {
    if (this._dataViewStickyTopFrame !== null) {
      return;
    }
    this._dataViewStickyTopFrame = requestAnimationFrame(() => {
      this._dataViewStickyTopFrame = null;
      this.syncDataViewStickyTop();
    });
  }

  private syncDataViewStickyTop() {
    if (!this.isDatabaseFullscreenSurface) {
      this.style.setProperty('--database-view-sticky-top', '0px');
      this.observeDataViewStickyHeader(null);
      return;
    }

    const header = this.querySelector<HTMLElement>(
      '[data-database-surface="fullscreen"]'
    );
    this.observeDataViewStickyHeader(header);

    const headerHeight = Math.ceil(header?.getBoundingClientRect().height ?? 0);
    this.style.setProperty('--database-view-sticky-top', `${headerHeight}px`);
  }

  private observeDataViewStickyHeader(header: HTMLElement | null) {
    if (this._dataViewStickyHeaderElement === header) {
      return;
    }

    this._dataViewStickyHeaderObserver?.disconnect();
    this._dataViewStickyHeaderElement = header;

    if (!header) {
      this._dataViewStickyHeaderObserver = null;
      return;
    }

    this._dataViewStickyHeaderObserver = new ResizeObserver(() => {
      this.scheduleDataViewStickyTopSync();
    });
    this._dataViewStickyHeaderObserver.observe(header);
  }

  private teardownDataViewStickyTopSync() {
    if (this._dataViewStickyTopFrame !== null) {
      cancelAnimationFrame(this._dataViewStickyTopFrame);
      this._dataViewStickyTopFrame = null;
    }
    this._dataViewStickyHeaderObserver?.disconnect();
    this._dataViewStickyHeaderObserver = null;
    this._dataViewStickyHeaderElement = null;
  }

  private isDatabaseStorageReady() {
    if (isDatabaseStorageModelReady(this.model)) {
      this.teardownDatabaseStorageReadyObserver();
      return true;
    }

    this.observeDatabaseStorageReady();
    return false;
  }

  private observeDatabaseStorageReady() {
    if (this._databaseStorageReadyCleanup) {
      return;
    }

    const rootDocId = this.model.props.databaseRootDocId;
    const stopObservingRootMissing =
      rootDocId && rootDocId !== this.model.store.id
        ? observeDatabaseSystemDocMissing(this.model.store, rootDocId, () => {
            if (this._databaseRootMissing) return;
            this._databaseRootMissing = true;
            this.requestUpdate();
          })
        : () => {};
    const stopObservingStorageReady = observeDatabaseStorageModelReady(
      this.model,
      () => {
        this._databaseRootMissing = false;
        this.teardownDatabaseStorageReadyObserver();
        this.ensureDatabaseRowFocusAwareness();
        this.ensureDatabaseStorageSubscriptions();
        this.ensureFileIndexHighlightIntegration();
        this.syncDatabaseInitialLoadReadyAttribute();
        this.rereadRefusedChanges();
        this.requestUpdate();
      }
    );
    this._databaseStorageReadyCleanup = () => {
      stopObservingRootMissing();
      stopObservingStorageReady();
    };
  }

  private teardownDatabaseStorageReadyObserver() {
    this._databaseStorageReadyCleanup?.();
    this._databaseStorageReadyCleanup = null;
  }

  private offlineCopyMenuItems() {
    const copy = this.offlineCopy$.value;
    if (!copy) return [];
    return [
      menu.toggleSwitch({
        prefix: LocalDataIcon(),
        name: dataViewI18nT('database.offlineCopy.setting'),
        on: copy.enabled,
        onChange: on => copy.setEnabled(on),
        testId: 'database-offline-copy-setting',
      }),
    ];
  }

  /** Row-sharded databases offer a device-local offline copy when the host
   * provides one; every view of the database shares it. */
  private ensureDatabaseOfflineCopy() {
    const port = this.optionsConfig.offlineCopy;
    if (this._releaseOfflineCopy || !port || !this.isConnected) return;
    const dataSource = this.dataSource.value;
    const storageModel = dataSource.storageModel;
    const rootDoc = storageModel.yBlock.doc;
    if (!rootDoc) return;
    // The copy covers every shard; it starts once all directory parts are
    // loaded and follows their changes.
    const manifest = () => {
      const directory = dataSource.getCompleteShardDirectory();
      return directory.status === 'ready' ? directory.manifest : undefined;
    };
    const { copy, release } = acquireDatabaseOfflineCopy(port, {
      databaseId: dataSource.databaseId,
      manifest,
      subscribe: onChange => {
        rootDoc.on('update', onChange);
        const stopParts = dataSource.onShardedRowsChange(onChange);
        return () => {
          rootDoc.off('update', onChange);
          stopParts();
        };
      },
    });
    this._releaseOfflineCopy = release;
    this.offlineCopy$.value = copy;
  }

  /** Follows the apps that read this database when the host reports them. */
  private ensureDatabaseAppUsage() {
    const port = this.optionsConfig.appUsage;
    if (this._stopAppUsageWatch || !port || !this.isConnected) return;
    const databaseId = this.dataSource.value.databaseId;
    this._stopAppUsageWatch = port.watch(databaseId, read => {
      this.appUsage$.value = read;
    });
  }

  /** The line of a queued trash that keeps the table visible. */
  protected renderDeletionPendingNotice(classes: {
    notice: string;
    message: string;
    button: string;
  }) {
    const source = blockPendingDeletionSource(this.model);
    const run = (action: Promise<unknown> | undefined) => {
      action?.catch(error => {
        console.error('[DatabaseBlock] Pending trash action failed:', error);
      });
    };
    return renderBlockPendingDeletionNotice(
      blockPendingDeletionState(this.model),
      {
        kind: 'database',
        text: key => dataViewI18nT(`database.deletionPending.${key}`),
        classes,
        usage: this.renderAppUsageBadge(),
        onCancel: () => run(source?.cancel(this.store.id, this.model.id)),
        onRetry: () => run(source?.retry(this.store.id, this.model.id)),
      }
    );
  }

  /** Top-left marker naming the apps that read this database; nothing
   * without a host port. */
  protected renderAppUsageBadge(canvas?: { className: string }) {
    if (!this.optionsConfig.appUsage) return nothing;
    return html`<affine-database-app-usage-badge
      class=${canvas?.className ?? nothing}
      data-canvas-db-zone=${canvas ? 'toolbar-action' : nothing}
      .read=${watch(this.appUsage$)}
    ></affine-database-app-usage-badge>`;
  }

  private ensureDatabaseStorageSubscriptions() {
    if (this.isDatabaseStorageReady()) {
      this.ensureDatabaseOfflineCopy();
      this.ensureDatabaseAppUsage();
    }
    if (
      this._databaseStorageSubscriptionsSetup ||
      !this.isConnected ||
      !this.isDatabaseStorageReady()
    ) {
      return;
    }

    this._databaseStorageSubscriptionsSetup = true;
    const storageModel = this.dataSource.value.storageModel;
    const subscription = storageModel.propsUpdated.subscribe(({ key }) => {
      if (
        key === 'activeViewId' ||
        key === 'canvasPresentation' ||
        key === 'columns' ||
        isDatabaseShardDirectoryProp(key) ||
        key === 'title' ||
        key === 'views'
      ) {
        this.requestUpdate();
      }
    });
    this.disposables.add(() => {
      subscription.unsubscribe();
      this._databaseStorageSubscriptionsSetup = false;
    });
  }

  private ensureDatabaseRowFocusAwareness() {
    if (
      this._databaseRowFocusAwarenessSetup ||
      !this.isConnected ||
      !this.isDatabaseStorageReady()
    ) {
      return;
    }

    this._databaseRowFocusAwarenessSetup = true;
    this.setupDatabaseRowFocusAwareness();
  }

  private setupDatabaseRowFocusAwareness() {
    const updateRemotePresences = () => {
      measureGfxPerformance(
        this.std,
        'database-presence-project',
        () => this.updateRemotePresences(),
        () => this.std.store.awarenessStore.getStates().size
      );
    };
    const updateLocalPresence = () => {
      this.updateLocalRowFocus();
      this.updateLocalCellPresenceFromSelection();
      this.updateLocalAreaSelectionPresenceFromSelection();
    };
    const updateLocalCellPresence = (
      event: Event & {
        detail?: DataViewCellPresenceDetail;
      }
    ) => {
      if (!event.detail) {
        return;
      }
      this.updateLocalCellPresence(event.detail);
    };

    this.disposables.add(
      this.rowFocusSelection$.subscribe(updateLocalPresence)
    );
    this.disposables.add(
      this.rowFocusDataSource.viewManager.currentViewId$.subscribe(() => {
        updateLocalPresence();
        updateRemotePresences();
      })
    );

    const awareness = this.std.store.awarenessStore.awareness;
    awareness.on('change', updateRemotePresences);
    this.addEventListener(
      DATA_VIEW_CELL_PRESENCE_EVENT,
      updateLocalCellPresence
    );
    this.disposables.add(() => {
      awareness.off('change', updateRemotePresences);
      this.removeEventListener(
        DATA_VIEW_CELL_PRESENCE_EVENT,
        updateLocalCellPresence
      );
      this.clearLocalRowFocus();
      this.clearLocalCellPresence();
      this.clearLocalAreaSelectionPresence();
      this._databaseRowFocusAwarenessSetup = false;
    });

    updateLocalPresence();
    updateRemotePresences();
  }

  private updateLocalCellPresenceFromSelection() {
    const selection = this.rowFocusSelection$.value;
    const cell = getCellPresenceFromSelection(
      selection,
      (viewId, rowIndex) =>
        getRowIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          rowIndex
        ),
      (viewId, columnIndex) =>
        getColumnIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          columnIndex
        )
    );
    if (!cell) {
      this.clearLocalCellPresence();
      return;
    }
    this.updateLocalCellPresence(cell);
  }

  private updateLocalAreaSelectionPresenceFromSelection() {
    const selection = this.rowFocusSelection$.value;
    const area = getAreaSelectionPresenceFromSelection(
      selection,
      (viewId, rowIndex) =>
        getRowIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          rowIndex
        ),
      (viewId, columnIndex) =>
        getColumnIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          columnIndex
        )
    );
    if (!area) {
      this.clearLocalAreaSelectionPresence();
      return;
    }

    const presenceKey = [
      this.databaseCellPresenceScope,
      this.blockId,
      area.viewId,
      area.rowIds.join(','),
      area.columnIds.join(','),
    ].join(':');
    if (presenceKey === this._lastLocalAreaSelectionPresenceKey) {
      return;
    }
    this._lastLocalAreaSelectionPresenceKey = presenceKey;

    this.std.store.awarenessStore.setLocalStateField(
      'databaseAreaSelectionPresence',
      {
        scope: this.databaseCellPresenceScope,
        databaseId: this.blockId,
        viewId: area.viewId,
        rowIds: area.rowIds,
        columnIds: area.columnIds,
        updatedAt: Date.now(),
      }
    );
  }

  private ensureFileIndexHighlightIntegration() {
    if (
      this._fileIndexHighlightIntegrationSetup ||
      !this.isConnected ||
      !this.isDatabaseStorageReady() ||
      !isFileIndexDatabase(this.model)
    ) {
      return;
    }

    this._fileIndexHighlightIntegrationSetup = true;
    ensureFileIndexRowHighlightStyles(this.ownerDocument);

    const publishSelectionHighlight = () => {
      const rowIds = this.getSelectedFileIndexRowIds(
        this.rowFocusSelection$.value
      );
      this.publishFileIndexRowSourceHighlight(
        FILE_INDEX_HIGHLIGHT_SCOPES.rowSelection,
        rowIds
      );
    };

    this.disposables.add(
      this.rowFocusSelection$.subscribe(publishSelectionHighlight)
    );

    const handlePointerOver = (event: PointerEvent) => {
      const rowId = this.getFileIndexRowIdFromEvent(event);
      if (this._fileIndexHoveredRowId === rowId) return;
      this._fileIndexHoveredRowId = rowId;
      this.publishFileIndexRowSourceHighlight(
        FILE_INDEX_HIGHLIGHT_SCOPES.rowHover,
        rowId ? [rowId] : []
      );
    };
    const handlePointerOut = (event: PointerEvent) => {
      const rowElement = this.getFileIndexRowElementFromEvent(event);
      if (!rowElement) return;
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && rowElement.contains(relatedTarget)) {
        return;
      }
      const relatedRowId = this.getFileIndexRowIdFromEventTarget(
        event.relatedTarget
      );
      if (relatedRowId && relatedRowId === rowElement.dataset.rowId) {
        return;
      }
      this._fileIndexHoveredRowId = null;
      this.publishFileIndexRowSourceHighlight(
        FILE_INDEX_HIGHLIGHT_SCOPES.rowHover,
        []
      );
    };
    this.addEventListener('pointerover', handlePointerOver);
    this.addEventListener('pointerout', handlePointerOut);
    this.disposables.add(() => {
      this.removeEventListener('pointerover', handlePointerOver);
      this.removeEventListener('pointerout', handlePointerOut);
      this._fileIndexHoveredRowId = null;
      this.publishFileIndexRowSourceHighlight(
        FILE_INDEX_HIGHLIGHT_SCOPES.rowHover,
        []
      );
      this.publishFileIndexRowSourceHighlight(
        FILE_INDEX_HIGHLIGHT_SCOPES.rowSelection,
        []
      );
    });

    const handleSourceHighlightChange = (event: Event): void => {
      const detail = (
        event as CustomEvent<FileIndexSourceElementHighlightChangeDetail>
      ).detail;
      if (detail?.docId && detail.docId !== this.store.id) return;
      this.updateFileIndexRowHighlightsForSources(detail?.sourceBlockIds ?? []);
    };
    window.addEventListener(
      FILE_INDEX_HIGHLIGHT_EVENTS.sourceElementHighlightChange,
      handleSourceHighlightChange
    );
    this.disposables.add(() => {
      window.removeEventListener(
        FILE_INDEX_HIGHLIGHT_EVENTS.sourceElementHighlightChange,
        handleSourceHighlightChange
      );
    });

    this._fileIndexRowHighlightObserver = new MutationObserver(() => {
      this.applyFileIndexRowHighlightAttributes();
    });
    this._fileIndexRowHighlightObserver.observe(this, {
      childList: true,
      subtree: true,
    });
    this.disposables.add(() => {
      this._fileIndexRowHighlightObserver?.disconnect();
      this._fileIndexRowHighlightObserver = null;
      this._fileIndexHighlightIntegrationSetup = false;
    });

    publishSelectionHighlight();
  }

  private getSelectedFileIndexRowIds(
    selection: DataViewSelection | undefined
  ): string[] {
    if (!selection || selection.type !== 'table') {
      return [];
    }

    if (selection.selectionType === 'row') {
      return selection.rows.map(row => row.id);
    }

    const view = this.rowFocusDataSource.viewManager.viewGet(selection.viewId);
    const start = Math.min(
      selection.rowsSelection.start,
      selection.rowsSelection.end
    );
    const end = Math.max(
      selection.rowsSelection.start,
      selection.rowsSelection.end
    );
    const rowIds: string[] = [];
    for (let rowIndex = start; rowIndex <= end; rowIndex += 1) {
      const rowId = getRowIdByViewIndex(view, rowIndex);
      if (rowId) rowIds.push(rowId);
    }
    return rowIds;
  }

  private publishFileIndexRowSourceHighlight(
    scope: FileIndexRowSourceHighlightScope,
    rowIds: readonly string[]
  ): void {
    const sourceBlockIds = getFileIndexSourceBlockIdsForRows(
      this.model,
      rowIds
    );
    this.dispatchEvent(
      new CustomEvent<FileIndexRowSourceHighlightChangeDetail>(
        FILE_INDEX_HIGHLIGHT_EVENTS.rowSourceHighlightChange,
        {
          bubbles: true,
          composed: true,
          detail: {
            databaseBlockId: this.blockId,
            docId: this.store.id,
            rowIds: [...rowIds],
            scope,
            sourceBlockIds,
          },
        }
      )
    );
  }

  private updateFileIndexRowHighlightsForSources(
    sourceBlockIds: readonly string[]
  ): void {
    this._fileIndexHighlightRetry?.();
    this._fileIndexHighlightRetry = null;
    const rowIds = getFileIndexRowIdsForSourceBlocks(
      this.model,
      sourceBlockIds
    );
    if (!rowIds) {
      // Highlights the rows once every directory part of the file index is
      // loaded.
      const dataSource = this.dataSource.value;
      dataSource.getCompleteShardDirectory();
      this._fileIndexHighlightRetry = dataSource.onShardedRowsChange(() =>
        this.updateFileIndexRowHighlightsForSources(sourceBlockIds)
      );
      this.clearFileIndexRowHighlights();
      return;
    }
    this._fileIndexHighlightedRowIds = new Set(rowIds);
    this.applyFileIndexRowHighlightAttributes();
  }

  private applyFileIndexRowHighlightAttributes(): void {
    const highlighted = this._fileIndexHighlightedRowIds;
    const current = this.querySelectorAll<HTMLElement>(
      `[${FILE_INDEX_ROW_HIGHLIGHT_ATTRIBUTE}="true"]`
    );
    for (const element of current) {
      const rowId = element.dataset.rowId;
      if (!rowId || !highlighted.has(rowId)) {
        element.removeAttribute(FILE_INDEX_ROW_HIGHLIGHT_ATTRIBUTE);
      }
    }

    for (const rowId of highlighted) {
      const escapedRowId = escapeDataAttributeSelectorValue(rowId);
      const targets = this.querySelectorAll<HTMLElement>(
        `data-view-table-row[data-row-id="${escapedRowId}"], ` +
          `dv-table-view-cell-container[data-row-id="${escapedRowId}"]`
      );
      for (const target of targets) {
        target.setAttribute(FILE_INDEX_ROW_HIGHLIGHT_ATTRIBUTE, 'true');
      }
    }
  }

  private clearFileIndexRowHighlights(): void {
    this._fileIndexHighlightedRowIds.clear();
    this.applyFileIndexRowHighlightAttributes();
  }

  private getFileIndexRowIdFromEvent(event: PointerEvent): string | null {
    return this.getFileIndexRowElementFromEvent(event)?.dataset.rowId ?? null;
  }

  private getFileIndexRowIdFromEventTarget(
    target: EventTarget | null
  ): string | null {
    if (!(target instanceof Element)) return null;
    return this.getFileIndexRowElementFromTarget(target)?.dataset.rowId ?? null;
  }

  private getFileIndexRowElementFromEvent(
    event: PointerEvent
  ): HTMLElement | null {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return this.getFileIndexRowElementFromTarget(target);
  }

  private getFileIndexRowElementFromTarget(
    target: Element
  ): HTMLElement | null {
    const element = target.closest<HTMLElement>(
      'data-view-table-row[data-row-id], dv-table-view-cell-container[data-row-id]'
    );
    return element && this.contains(element) ? element : null;
  }

  private updateLocalRowFocus() {
    const selection = this.rowFocusSelection$.value;
    const rowId = getRowFocusIdFromSelection(selection, (viewId, rowIndex) =>
      getRowIdByViewIndex(
        this.rowFocusDataSource.viewManager.viewGet(viewId),
        rowIndex
      )
    );

    if (!selection || !rowId) {
      this.clearLocalRowFocus();
      return;
    }

    const focusKey = [
      this.databaseRowFocusScope,
      this.blockId,
      selection.viewId,
      rowId,
    ].join(':');
    if (focusKey === this._lastLocalRowFocusKey) {
      return;
    }
    this._lastLocalRowFocusKey = focusKey;

    this.std.store.awarenessStore.setLocalStateField('databaseRowFocus', {
      scope: this.databaseRowFocusScope,
      databaseId: this.blockId,
      viewId: selection.viewId,
      rowId,
      updatedAt: Date.now(),
    });
  }

  private clearLocalRowFocus() {
    const previousKey = this._lastLocalRowFocusKey;
    if (!previousKey) {
      return;
    }
    const current =
      this.std.store.awarenessStore.getLocalState()?.databaseRowFocus;
    this._lastLocalRowFocusKey = null;

    const currentKey = current
      ? [current.scope, current.databaseId, current.viewId, current.rowId].join(
          ':'
        )
      : null;
    if (currentKey !== previousKey) {
      return;
    }

    this.std.store.awarenessStore.setLocalStateField(
      'databaseRowFocus',
      undefined
    );
  }

  private updateLocalCellPresence(presence: DataViewCellPresenceDetail) {
    const selection = this.rowFocusSelection$.value;
    const expected = getCellPresenceFromSelection(
      selection,
      (viewId, rowIndex) =>
        getRowIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          rowIndex
        ),
      (viewId, columnIndex) =>
        getColumnIdByViewIndex(
          this.rowFocusDataSource.viewManager.viewGet(viewId),
          columnIndex
        )
    );
    if (
      !expected ||
      presence.viewId !== expected.viewId ||
      presence.rowId !== expected.rowId ||
      presence.columnId !== expected.columnId ||
      presence.mode !== expected.mode
    ) {
      this.clearLocalCellPresence();
      return;
    }

    const presenceKey = [
      this.databaseCellPresenceScope,
      this.blockId,
      presence.viewId,
      presence.rowId,
      presence.columnId,
      presence.mode,
      presence.caretOffset ?? '',
      presence.caretLength ?? '',
    ].join(':');
    if (presenceKey === this._lastLocalCellPresenceKey) {
      return;
    }
    this._lastLocalCellPresenceKey = presenceKey;

    this.std.store.awarenessStore.setLocalStateField('databaseCellPresence', {
      caretLength: presence.caretLength,
      caretOffset: presence.caretOffset,
      scope: this.databaseCellPresenceScope,
      databaseId: this.blockId,
      viewId: presence.viewId,
      rowId: presence.rowId,
      columnId: presence.columnId,
      mode: presence.mode,
      updatedAt: Date.now(),
    });
  }

  private clearLocalCellPresence() {
    const previousKey = this._lastLocalCellPresenceKey;
    if (!previousKey) {
      return;
    }
    const current =
      this.std.store.awarenessStore.getLocalState()?.databaseCellPresence;
    this._lastLocalCellPresenceKey = null;

    const currentKey = current
      ? [
          current.scope,
          current.databaseId,
          current.viewId,
          current.rowId,
          current.columnId,
          current.mode,
          current.caretOffset ?? '',
          current.caretLength ?? '',
        ].join(':')
      : null;
    if (currentKey !== previousKey) {
      return;
    }

    this.std.store.awarenessStore.setLocalStateField(
      'databaseCellPresence',
      undefined
    );
  }

  private clearLocalAreaSelectionPresence() {
    const previousKey = this._lastLocalAreaSelectionPresenceKey;
    if (!previousKey) {
      return;
    }
    const current =
      this.std.store.awarenessStore.getLocalState()
        ?.databaseAreaSelectionPresence;
    this._lastLocalAreaSelectionPresenceKey = null;

    const currentKey = current
      ? [
          current.scope,
          current.databaseId,
          current.viewId,
          current.rowIds.join(','),
          current.columnIds.join(','),
        ].join(':')
      : null;
    if (currentKey !== previousKey) {
      return;
    }

    this.std.store.awarenessStore.setLocalStateField(
      'databaseAreaSelectionPresence',
      undefined
    );
  }

  private updateRemotePresences() {
    const awarenessStore = this.std.store.awarenessStore;
    const currentViewId =
      this.rowFocusDataSource.viewManager.currentViewId$.value;
    const localClientId = awarenessStore.awareness.clientID;
    const focuses: DataViewRemoteRowFocus[] = [];
    const presences: DataViewRemoteCellPresence[] = [];
    const selections: DataViewRemoteAreaSelectionPresence[] = [];

    awarenessStore.getStates().forEach((state, clientId) => {
      if (clientId === localClientId) {
        return;
      }
      const focus = state.databaseRowFocus;
      if (
        focus &&
        focus.scope === this.databaseRowFocusScope &&
        focus.databaseId === this.blockId &&
        focus.viewId === currentViewId
      ) {
        focuses.push({
          clientId,
          color: state.color ?? 'var(--streamlain-v2-brand-primary)',
          rowId: focus.rowId,
          updatedAt: focus.updatedAt,
          userName: state.user?.name,
        });
      }

      const presence = state.databaseCellPresence;
      if (
        presence &&
        presence.scope === this.databaseCellPresenceScope &&
        presence.databaseId === this.blockId &&
        cellPresenceIsForView(presence.viewId, currentViewId)
      ) {
        presences.push({
          caretLength: presence.caretLength,
          caretOffset: presence.caretOffset,
          clientId,
          color: state.color ?? 'var(--streamlain-v2-brand-primary)',
          columnId: presence.columnId,
          mode: presence.mode,
          rowId: presence.rowId,
          surface: presence.surface,
          updatedAt: presence.updatedAt,
          userAvatar: state.user?.avatar,
          userName: state.user?.name,
        });
      }

      const selection = state.databaseAreaSelectionPresence;
      if (
        selection &&
        selection.scope === this.databaseCellPresenceScope &&
        selection.databaseId === this.blockId &&
        selection.viewId === currentViewId
      ) {
        selections.push({
          clientId,
          color: state.color ?? 'var(--streamlain-v2-brand-primary)',
          columnIds: selection.columnIds,
          rowIds: selection.rowIds,
          updatedAt: selection.updatedAt,
          userAvatar: state.user?.avatar,
          userName: state.user?.name,
        });
      }
    });

    const assistants = readAssistantDatabasePresences(
      awarenessStore.getStates(),
      this.blockId
    );
    focuses.push(...assistants.focuses);
    presences.push(...assistants.cells);

    focuses.sort((a, b) => a.updatedAt - b.updatedAt);
    selections.sort((a, b) => a.updatedAt - b.updatedAt);
    presences.sort((a, b) => a.updatedAt - b.updatedAt);
    if (
      !areDataViewRemotePresencesEqual(this.remoteRowFocuses$.value, focuses)
    ) {
      this.remoteRowFocuses$.value = focuses;
    }
    if (
      !areDataViewRemotePresencesEqual(
        this.remoteCellPresences$.value,
        presences
      )
    ) {
      this.remoteCellPresences$.value = presences;
    }
    if (
      !areDataViewRemotePresencesEqual(
        this.remoteAreaSelections$.value,
        selections
      )
    ) {
      this.remoteAreaSelections$.value = selections;
    }
  }

  /**
   * Streamlain: Check if database should render in current mode.
   * In page mode, hide databases that are:
   * 1. Created directly on canvas (parent is @root, has xywh coordinates)
   * 2. Inside EdgelessOnly notes
   */
  private shouldRenderInPageMode(): boolean {
    const docMode = this.std.getOptional(DocModeProvider)?.getEditorMode();
    if (docMode !== 'page') return true; // In edgeless mode, always render

    const parent = this.store.getParent(this.model.id);

    // Case 1: Database directly under @root (canvas-created via toolbar drag)
    // These have xywh coordinates and should NOT appear in page mode
    if (!parent || parent.flavour === 'affine:page') {
      // Check if this database has canvas positioning (xywh)
      const xywh = (this.model.props as Record<string, unknown>).xywh;
      if (xywh) {
        return false; // Canvas database with position → hide in page mode
      }
      return true; // No xywh → probably a doc-mode database
    }

    // Case 2: Database under a Note - check note's displayMode
    if (parent.flavour === 'affine:note') {
      const displayMode = (parent as NoteBlockModel).props.displayMode;
      if (displayMode === NoteDisplayMode.EdgelessOnly) {
        return false; // EdgelessOnly note → hide in page mode
      }
    }

    return true;
  }

  listenFullWidthChange() {
    if (this.std.get(DocModeProvider).getEditorMode() === 'edgeless') {
      return;
    }
    this.disposables.add(
      autoUpdate(this.host, this, () => {
        const padding =
          this.getBoundingClientRect().left -
          this.host.getBoundingClientRect().left;
        this.virtualPadding$.value = Math.max(0, padding - 72);
      })
    );
  }

  private readonly dataViewRootLogic = lazy(
    () =>
      new DataViewRootUILogic({
        performanceScope: this.std,
        virtualPadding$: this.virtualPadding$,
        bindHotkey: hotkeys => {
          return {
            dispose: this.host.event.bindHotkey(hotkeys, {
              blockId: this.topContenteditableElement?.blockId ?? this.blockId,
            }),
          };
        },
        handleEvent: (name, handler) => {
          return {
            dispose: this.host.event.add(name, handler, {
              blockId: this.blockId,
            }),
          };
        },
        selection$: this.viewSelection$,
        remoteAreaSelections$: this.remoteAreaSelections$,
        remoteRowFocuses$: this.remoteRowFocuses$,
        remoteCellPresences$: this.remoteCellPresences$,
        setSelection: this.setSelection,
        preferDesktopRenderer: true,
        dataSource: this.dataSource.value,
        headerWidget: this.headerWidget,
        footerWidget: this.footerWidget,
        footerProgress$: this.offlineCopyFooterProgress$,
        onDrag: this.onDrag,
        clipboard: this.std.clipboard,
        notification: {
          toast: message => {
            const notification = this.std.getOptional(NotificationProvider);
            if (notification) {
              notification.toast(message);
            } else {
              toast(this.host, message);
            }
          },
        },
        eventTrace: (key, params) => {
          const telemetryService = this.std.getOptional(TelemetryProvider);
          telemetryService?.track(key, {
            ...(params as TelemetryEventMap[typeof key]),
            blockId: this.blockId,
          });
        },
        detailPanelConfig: {
          openDetailPanel: (target, data) => {
            const peekViewService = this.std.getOptional(PeekViewProvider);
            if (peekViewService) {
              const openDoc = (docId: string) => {
                return peekViewService.peek({
                  docId,
                  databaseId: this.blockId,
                  databaseDocId: this.model.store.id,
                  databaseRowId: data.rowId,
                  target: this,
                });
              };
              const doc = getSingleDocIdFromText(
                this.model.store.getBlock(data.rowId)?.model?.text
              );
              if (doc) {
                return openDoc(doc);
              }
              const abort = new AbortController();
              return new Promise<void>(focusBack => {
                peekViewService
                  .peek(
                    {
                      target,
                      template: this.createTemplate(data, docId => {
                        // abort.abort();
                        openDoc(docId).then(focusBack).catch(focusBack);
                      }),
                    },
                    { abortSignal: abort.signal }
                  )
                  .then(focusBack)
                  .catch(focusBack);
              });
            } else {
              return popSideDetail(
                this.createTemplate(data, () => {
                  //
                })
              );
            }
          },
        },
      })
  );
  override renderBlock() {
    const readiness = this.getDatabaseInitialLoadReadiness();
    this.syncDatabaseInitialLoadReadyAttribute(readiness.status === 'ready');
    if (readiness.status === 'ready') this.confirmRestoredDataArrived();
    if (readiness.status === 'loading') {
      return nothing;
    }
    const deletionPendingNotice = this.renderDeletionPendingNotice({
      notice: `${databaseShardErrorStyles} ${databaseRefusedChangesStyles}`,
      message: 'database-shard-error-message',
      button: 'database-shard-error-action',
    });
    if (readiness.status === 'error') {
      return html`
        ${deletionPendingNotice}
        ${this.renderRefusedChangesNotice({
          notice: `${databaseShardErrorStyles} ${databaseRefusedChangesStyles}`,
          title: 'database-shard-error-title',
          message: 'database-shard-error-message',
          actions: 'database-shard-error-actions',
          button: 'database-shard-error-action',
          primaryButton:
            'database-shard-error-action database-shard-error-action-primary',
          status: 'database-shard-error-outcome',
        })}
        <div
          contenteditable="false"
          class="${databaseContentStyles} ${databaseShardErrorStyles}"
          data-testid="database-row-shard-error"
          role="alert"
        >
          <div class="database-shard-error-title">
            ${readiness.missing
              ? dataViewI18nT('database.dataMissing.title')
              : dataViewI18nT('database.shardRepair.title')}
          </div>
          <div class="database-shard-error-message">${readiness.message}</div>
          ${readiness.missing
            ? this.renderMissingDataActions(
                this.createMissingDataRepairRequest(
                  this._databaseRootMissing ? 'root' : 'rows',
                  this.isDatabaseStorageReady()
                    ? this.dataSource.value.missingRowShardDocIds
                    : []
                ),
                {
                  actions: 'database-shard-error-actions',
                  button: 'database-shard-error-action',
                  primaryButton:
                    'database-shard-error-action database-shard-error-action-primary',
                  status: 'database-shard-error-outcome',
                }
              )
            : nothing}
          ${readiness.repairable
            ? html`
                <div class="database-shard-error-consequence">
                  ${dataViewI18nT('database.shardRepair.consequence')}
                </div>
                ${!this.dataSource.value.readonly$.value
                  ? html`
                      ${repairActionButton({
                        action: 'database.shard-manifest',
                        className: 'database-shard-error-action',
                        label: dataViewI18nT('database.shardRepair.action'),
                        onClick: this.repairRowShardManifestDrift,
                        testId: 'database-row-shard-repair',
                      })}
                    `
                  : nothing}
              `
            : nothing}
        </div>
      `;
    }
    this.ensureDatabaseRowFocusAwareness();
    this.ensureFileIndexHighlightIntegration();

    const widgets = html`${repeat(
      Object.entries(this.widgets),
      ([id]) => id,
      ([_, widget]) => widget
    )}`;

    const refusedChangesNotice = this.renderRefusedChangesNotice({
      notice: `${databaseShardErrorStyles} ${databaseRefusedChangesStyles}`,
      title: 'database-shard-error-title',
      message: 'database-shard-error-message',
      actions: 'database-shard-error-actions',
      button: 'database-shard-error-action',
      primaryButton:
        'database-shard-error-action database-shard-error-action-primary',
      status: 'database-shard-error-outcome',
    });
    const inlineLargeDataPreview = this.renderInlineLargeDataPreview();
    if (inlineLargeDataPreview !== nothing) {
      return html`
        ${deletionPendingNotice} ${refusedChangesNotice}
        <div contenteditable="false" class="${databaseContentStyles}">
          ${inlineLargeDataPreview} ${widgets}
        </div>
      `;
    }

    return html`
      ${deletionPendingNotice} ${refusedChangesNotice}
      <div contenteditable="false" class="${databaseContentStyles}">
        ${this.dataViewRootLogic.value.render()} ${widgets}
      </div>
    `;
  }

  override accessor useZeroWidth = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-database': DatabaseBlockComponent;
  }
}
